﻿import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  addBusinessBranch,
  customerSignUp,
  deleteBusinessBranch,
  fetchMyBusinessOnboarding,
  getGoogleSignUpUrl,
  resendCustomerEmailOtp,
  sendCustomerPhoneOtp,
  submitBusinessOnboarding,
  updateBusinessBranch,
  upsertBusinessProfile,
  uploadCustomerFaceLiveness,
  verifyCustomerEmail,
  verifyCustomerPhoneOtp,
} from '../services/api/customerAuth';
import {
  completeDriverPersonalDocs,
  completeDriverVehicleInformation,
  fetchDriverByEmail,
  driverSignUp,
  setDriverAccountType as saveDriverAccountType,
  getDriverGoogleSignUpUrl,
  resendDriverEmailOtp,
  sendDriverPhoneOtp,
  submitDriverBankDetails,
  submitDriverCompanyDetails,
  submitDriverOnboarding,
  submitDriverVehicleDetails,
  uploadDriverBankLetter,
  uploadDriverCriminalRecord,
  uploadDriverFaceLiveness,
  uploadDriverIdCopy,
  uploadDriverInsuranceLetter,
  uploadDriverLicenceDisk,
  uploadDriverPdp,
  uploadDriverPhoto,
  uploadDriverRoadworthy,
  uploadDriverVehiclePhoto,
  verifyDriverEmail,
  verifyDriverPhoneOtp,
} from '../services/api/driverAuth';
import {
  clearCustomerAuthFlow,
  getCustomerAuthToken,
  getCustomerAuthUser,
  saveCustomerAuthTokens,
  saveCustomerAuthUser,
} from '../services/storage/customerFlow';
import {
  clearDriverAuthFlow,
  getDriverAuthToken,
  getDriverAuthUser,
  saveDriverAuthTokens,
  saveDriverAuthUser,
} from '../services/storage/driverFlow';
import { BusinessProfile, CustomerUser } from '../types/customerAuth';
import {
  DriverAccountType,
  DriverBankDetailsRequest,
  DriverCompanyDetailsRequest,
  DriverUser,
  DriverVehicleDetailsRequest,
  DriverVehicleType,
} from '../types/driverAuth';
import { nextStageForCustomer, RegisterFlowStage } from '../utils/customerOnboarding';
import { DriverRegisterFlowStage, nextStageForDriver } from '../utils/driverOnboarding';
import { DRIVER_SELF_SERVICE_ONBOARDING_ENABLED } from '../config/env';
import GooglePlayIcon from '../components/common/GooglePlayIcon';
import { USER_APP_URL } from '../config/appLinks';
import { ensureGooglePlacesLoaded } from '../services/googlePlaces';
import '../styles/register.css';

type AppRole = 'customer' | 'driver';
type RoleOptionId = AppRole | 'company' | 'agent';
type ChallengeType = 'STRAIGHT' | 'LEFT' | 'RIGHT';
type CaptureState = Partial<Record<ChallengeType, { blob: Blob; previewUrl: string }>>;
type SnackbarTone = 'success' | 'error' | 'info';
type RegistrationStage = RegisterFlowStage | DriverRegisterFlowStage;

interface RoleOption {
  id: RoleOptionId;
  title: string;
  description: string;
  badge: string;
  note: string;
  available: boolean;
}

interface SnackbarState {
  message: string;
  tone: SnackbarTone;
}

interface DriverTownPrediction {
  description: string;
  placeId: string;
  primaryText: string;
  secondaryText: string;
}

interface PickedDriverTown {
  address: string;
  lat: number;
  lng: number;
  province: string;
  town: string;
}

interface UploadField {
  file: File | null;
  label: string;
}

interface GoogleAddressComponent {
  long_name?: string;
  types?: string[];
}

const DRIVER_APP_URL =
  'https://expo.dev/accounts/shiftwagon7/projects/shift-wagon/builds/2dbc3acf-d4c5-45d7-b725-c166cb74d0c3';
const DRIVER_PARTNER_CONTACT_PATH = '/about#contact';
const ZA_PROVINCES = [
  'Eastern Cape',
  'Free State',
  'Gauteng',
  'KwaZulu-Natal',
  'Limpopo',
  'Mpumalanga',
  'Northern Cape',
  'North West',
  'Western Cape',
] as const;

const DRIVER_VEHICLE_OPTIONS: Array<{
  value: DriverVehicleType;
  label: string;
  hint: string;
}> = [
  { value: 'BAKKIE', label: 'Bakkie', hint: 'Best for smaller loads, furniture, and local deliveries.' },
  { value: 'PANEL_VAN', label: 'Panel Van', hint: 'Ideal for secure urban deliveries and business stock.' },
  { value: 'TRUCK_4_TON', label: '4 Ton Truck', hint: 'A good fit for regular medium-load moves.' },
  { value: 'MEDIUM_TRUCK', label: 'Medium Truck', hint: 'Useful for larger deliveries and growing business demand.' },
  { value: 'TRUCK_14_TON', label: '14 Ton Truck', hint: 'Designed for heavier loads and larger commercial jobs.' },
  { value: 'SEMI_TRUCK', label: 'Semi Truck', hint: 'For long-haul and larger freight jobs.' },
  { value: 'HORSE_AND_TRAILER', label: 'Horse And Trailer', hint: 'For high-capacity line-haul operations.' },
];

const DRIVER_ACCOUNT_TYPE_OPTIONS: Array<{
  value: DriverAccountType;
  title: string;
  description: string;
  badge: string;
  bestFor: string;
  points: string[];
}> = [
  {
    value: 'INDIVIDUAL_OWNER',
    badge: 'IO',
    bestFor: 'Best for owner-drivers and independent operators',
    title: 'Individual Owner',
    description: 'You own or operate your own vehicle and receive payouts into your bank account.',
    points: [
      'Use your personal or business bank account for payouts',
      'Great for self-managed drivers with their own vehicle',
      'Web onboarding ends with bank details before submission',
    ],
  },
  {
    value: 'COMPANY_DRIVER',
    badge: 'CD',
    bestFor: 'Best for drivers operating under a company',
    title: 'Company Driver',
    description: 'You drive under a company and we keep company contact details on your profile.',
    points: [
      'Attach your profile to a company contact structure',
      'Ideal when assignments and operations run through a fleet business',
      'Web onboarding ends with company details before submission',
    ],
  },
];

const LIVENESS_STEPS: Array<{
  key: ChallengeType;
  title: string;
  hint: string;
}> = [
  {
    key: 'STRAIGHT',
    title: 'Center your face',
    hint: 'Look straight into the camera and keep your full face inside the frame.',
  },
  {
    key: 'LEFT',
    title: 'Turn slightly left',
    hint: 'Turn your head slightly left while keeping your face visible.',
  },
  {
    key: 'RIGHT',
    title: 'Turn slightly right',
    hint: 'Turn your head slightly right while keeping your face visible.',
  },
];

const DRIVER_PROGRESS_STEPS: Array<{
  stage: DriverRegisterFlowStage;
  label: string;
}> = [
  { stage: 'account', label: 'Account' },
  { stage: 'driver-location', label: 'Town' },
  { stage: 'verify-email', label: 'Email' },
  { stage: 'phone-verification', label: 'Phone' },
  { stage: 'face-liveness', label: 'Face' },
  { stage: 'personal-docs', label: 'Docs' },
  { stage: 'vehicle-details', label: 'Vehicle' },
  { stage: 'vehicle-photos', label: 'Files' },
  { stage: 'account-type', label: 'Type' },
  { stage: 'company-details', label: 'Company' },
  { stage: 'bank-details', label: 'Bank' },
  { stage: 'submitted', label: 'Submit' },
  { stage: 'completed', label: 'Ready' },
];

function normalizePhoneToE164(phone: string): string {
  const digitsOnly = phone.replace(/[^\d]/g, '');
  return digitsOnly ? `+${digitsOnly}` : '';
}

function normalizePhoneForInput(phone: string | null | undefined): string {
  return String(phone ?? '').replace(/^\+/, '');
}

function fileToLabel(file: File | null): string {
  return file?.name || '';
}

function normalizeDigits(value: string): string {
  return value.replace(/[^\d]/g, '');
}

function formatDateForApi(value: string): string {
  return value;
}

function getTodayDateInputValue(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function isFutureDateInputValue(value: string): boolean {
  return Boolean(value) && value > getTodayDateInputValue();
}

function isBusinessCustomer(user: CustomerUser | null | undefined): boolean {
  return user?.accountType === 'BUSINESS';
}

function emptyToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function extractTownFromPlace(place: any): string | null {
  const components = (place.address_components ?? []) as GoogleAddressComponent[];
  const find = (type: string) =>
    components.find((component: GoogleAddressComponent) => component.types?.includes(type))?.long_name;

  return (
    find('locality') ||
    find('sublocality') ||
    find('administrative_area_level_2') ||
    place.name ||
    null
  );
}

function extractProvinceFromPlace(place: any): string | null {
  const components = (place.address_components ?? []) as GoogleAddressComponent[];
  return (
    components.find((component: GoogleAddressComponent) => component.types?.includes('administrative_area_level_1'))
      ?.long_name || null
  );
}


const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialRole = searchParams.get('role') === 'driver' ? 'driver' : 'customer';

  const [selectedRole, setSelectedRole] = useState<AppRole>(initialRole);
  const [stage, setStage] = useState<RegistrationStage>('account');
  const [customerUser, setCustomerUser] = useState<CustomerUser | null>(null);
  const [customerAccountType, setCustomerAccountType] = useState<'INDIVIDUAL' | 'BUSINESS'>('INDIVIDUAL');
  const [businessProfile, setBusinessProfile] = useState<BusinessProfile | null>(null);
  const [businessProfileForm, setBusinessProfileForm] = useState({
    legalName: '',
    tradingName: '',
    registrationNumber: '',
    vatNumber: '',
    businessEmail: '',
    businessPhone: '',
    headOfficeAddress: '',
    representativeName: '',
    representativeEmail: '',
  });
  const [businessBranchForm, setBusinessBranchForm] = useState({
    id: '',
    branchName: '',
    address: '',
    contactEmail: '',
    contactPhone: '',
    defaultBranch: false,
  });
  const [showBusinessBranchForm, setShowBusinessBranchForm] = useState(false);
  const [businessLoading, setBusinessLoading] = useState(false);
  const [driverUser, setDriverUser] = useState<DriverUser | null>(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
  });
  const [driverLocation, setDriverLocation] = useState({
    province: 'Gauteng',
    country: 'South Africa',
  });
  const [pickedDriverTown, setPickedDriverTown] = useState<PickedDriverTown | null>(null);
  const [showTownSearchModal, setShowTownSearchModal] = useState(false);
  const [townSearchQuery, setTownSearchQuery] = useState('');
  const [townPredictions, setTownPredictions] = useState<DriverTownPrediction[]>([]);
  const [townSearchLoading, setTownSearchLoading] = useState(false);
  const [emailCode, setEmailCode] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [driverPersonalDocs, setDriverPersonalDocs] = useState({
    criminalIssuedOn: '',
    criminalRecord: { file: null, label: '' } as UploadField,
    driverPhoto: { file: null, label: '' } as UploadField,
    idCopy: { file: null, label: '' } as UploadField,
  });
  const [driverVehicleDetails, setDriverVehicleDetails] = useState({
    color: '',
    insuranceBusinessUseConfirmed: null as boolean | null,
    make: '',
    model: '',
    registrationNumber: '',
    vehicleType: 'TRUCK_4_TON' as DriverVehicleType,
    year: '',
  });
  const [driverVehicleFiles, setDriverVehicleFiles] = useState({
    backPhoto: { file: null, label: '' } as UploadField,
    frontPhoto: { file: null, label: '' } as UploadField,
    insuranceLetter: { file: null, label: '' } as UploadField,
    leftPhoto: { file: null, label: '' } as UploadField,
    licenceDisk: { file: null, label: '' } as UploadField,
    licenceDiskIssuedOn: '',
    licenceDiskValidTo: '',
    pdp: { file: null, label: '' } as UploadField,
    pdpIssuedOn: '',
    pdpValidTo: '',
    rightPhoto: { file: null, label: '' } as UploadField,
    roadworthy: { file: null, label: '' } as UploadField,
    roadworthyIssuedOn: '',
  });
  const [driverAccountType, setDriverAccountType] = useState<DriverAccountType | null>(null);
  const [driverCompanyDetails, setDriverCompanyDetails] = useState({
    companyEmail: '',
    companyName: '',
    companyPhone1: '',
    companyPhone2: '',
  });
  const [driverBankDetails, setDriverBankDetails] = useState({
    accountHolderName: '',
    accountNumber: '',
    bankLetter: { file: null, label: '' } as UploadField,
    bankName: '',
    branchCode: '',
  });
  const [loading, setLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [driverStageLoading, setDriverStageLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [cameraError, setCameraError] = useState('');
  const [capturing, setCapturing] = useState(false);
  const [uploadingLiveness, setUploadingLiveness] = useState(false);
  const [currentLivenessIndex, setCurrentLivenessIndex] = useState(0);
  const [captures, setCaptures] = useState<CaptureState>({});
  const [snackbar, setSnackbar] = useState<SnackbarState | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const todayDateInputValue = getTodayDateInputValue();

  const roles: RoleOption[] = useMemo(
    () => [
      {
        id: 'customer',
        title: 'Customer',
        description: 'Book trucks for moves and deliveries.',
        badge: 'U',
        note: 'Customer app',
        available: true,
      },
      {
        id: 'driver',
        title: 'Driver',
        description: DRIVER_SELF_SERVICE_ONBOARDING_ENABLED
          ? 'Join the marketplace and receive jobs.'
          : 'Apply as an approved transport partner.',
        badge: 'D',
        note: DRIVER_SELF_SERVICE_ONBOARDING_ENABLED ? 'Driver app' : 'Contact-led onboarding',
        available: true,
      },
        {
          id: 'company',
          title: 'Company Owner',
          description: 'Manage company drivers and fleet activity.',
          badge: 'C',
          note: 'Company dashboard',
          available: true,
        },
      {
        id: 'agent',
        title: 'Agent',
        description: 'Support onboarding and local operations.',
        badge: 'A',
        note: 'Agent onboarding coming soon',
        available: false,
      },
    ],
    [],
  );

  const selectedRoleData = roles.find((role) => role.id === selectedRole) ?? roles[0];
  const roleParamValue = searchParams.get('role');
  const isExplicitCustomerRegistration = selectedRole === 'customer' && roleParamValue === 'customer';
  const driverSelfServiceOpen = DRIVER_SELF_SERVICE_ONBOARDING_ENABLED;
  const isDriverPublicOnboardingClosed = selectedRole === 'driver' && !driverSelfServiceOpen;
  const googleSignUpUrl =
    selectedRole === 'driver' && driverSelfServiceOpen ? getDriverGoogleSignUpUrl() : getGoogleSignUpUrl();
  const currentLivenessStep = LIVENESS_STEPS[currentLivenessIndex];
  const livenessComplete = LIVENESS_STEPS.every((stepConfig) => Boolean(captures[stepConfig.key]));
  const activeUser = selectedRole === 'driver' ? driverUser : customerUser;
  const signedUpBusinessName = customerUser?.accountType === 'BUSINESS'
    ? String(customerUser.name ?? '').trim()
    : '';
  const driverProgressIndex = DRIVER_PROGRESS_STEPS.findIndex((item) => item.stage === stage);
  const driverOnboardingStep = driverUser?.onboardingsStep?.toUpperCase();
  const isDriverOnboardingSubmittedOrCompleted =
    driverOnboardingStep === 'SUBMITTED' || driverOnboardingStep === 'COMPLETED';

  const applyBusinessProfile = (profile: BusinessProfile) => {
    const businessName = signedUpBusinessName;
    const businessEmail = String(customerUser?.email ?? '').trim();

    setBusinessProfile(profile);
    setCustomerUser((current) => {
      if (!current || current.accountType !== 'BUSINESS') return current;

      const nextUser = {
        ...current,
        businessProfileId: profile.id,
        businessVerificationStatus: profile.verificationStatus,
      };
      saveCustomerAuthUser(nextUser);
      return nextUser;
    });
    setBusinessProfileForm({
      legalName: profile.legalName || businessName,
      tradingName: profile.tradingName || businessName,
      registrationNumber: profile.registrationNumber || '',
      vatNumber: profile.vatNumber || '',
      businessEmail: profile.businessEmail || businessEmail,
      businessPhone: profile.businessPhone || '',
      headOfficeAddress: profile.headOfficeAddress || '',
      representativeName: profile.representativeName || '',
      representativeEmail: profile.representativeEmail || '',
    });

    const defaultBranch = profile.branches.find((branch) => branch.defaultBranch) ?? profile.branches[0];
    if (defaultBranch) {
      setBusinessBranchForm({
        id: defaultBranch.id,
        branchName: defaultBranch.branchName || '',
        address: defaultBranch.address || '',
        contactEmail: defaultBranch.contactEmail || '',
        contactPhone: defaultBranch.contactPhone || '',
        defaultBranch: defaultBranch.defaultBranch,
      });
      setShowBusinessBranchForm(false);
    } else {
      setBusinessBranchForm({
        id: '',
        branchName: '',
        address: '',
        contactEmail: '',
        contactPhone: '',
        defaultBranch: false,
      });
      setShowBusinessBranchForm(false);
    }
  };

  useEffect(() => {
    const roleParam = searchParams.get('role') === 'driver' ? 'driver' : 'customer';
    setSelectedRole(roleParam);

    if (!searchParams.get('resume')) return;

    if (roleParam === 'driver') {
      const storedDriver = getDriverAuthUser();
      const storedToken = getDriverAuthToken();
      if (!storedDriver || !storedToken) return;

      setDriverUser(storedDriver);
      setCustomerUser(null);
      setFormData((current) => ({
        ...current,
        email: storedDriver.email || current.email,
        fullName: storedDriver.name || current.fullName,
      }));
      setDriverLocation({
        country: storedDriver.homeBaseCountry || 'South Africa',
        province: storedDriver.homeBaseProvince || 'Gauteng',
      });
      if (storedDriver.homeBaseTown && storedDriver.homeBaseLat != null && storedDriver.homeBaseLng != null) {
        setPickedDriverTown({
          address: storedDriver.homeBaseTown,
          lat: Number(storedDriver.homeBaseLat),
          lng: Number(storedDriver.homeBaseLng),
          province: storedDriver.homeBaseProvince || 'Gauteng',
          town: storedDriver.homeBaseTown,
        });
      }
      setPhone(normalizePhoneForInput(storedDriver.phoneE164));
      setDriverAccountType(storedDriver.accountType || null);
      const vehicle = storedDriver.vehicles?.[0];
      if (vehicle) {
        setDriverVehicleDetails({
          color: vehicle.vehicleColor || '',
          insuranceBusinessUseConfirmed: vehicle.insuranceBusinessUseConfirmed ?? null,
          make: vehicle.make || '',
          model: vehicle.model || '',
          registrationNumber: vehicle.registrationNumber || '',
          vehicleType: (vehicle.vehicleType as DriverVehicleType) || 'TRUCK_4_TON',
          year: vehicle.year ? String(vehicle.year) : '',
        });
      }
      setDriverCompanyDetails({
        companyEmail: storedDriver.companyDetails?.companyEmail || '',
        companyName: storedDriver.companyDetails?.companyName || '',
        companyPhone1: normalizePhoneForInput(storedDriver.companyDetails?.companyPhone1E164),
        companyPhone2: normalizePhoneForInput(storedDriver.companyDetails?.companyPhone2E164),
      });
      setDriverBankDetails((current) => ({
        ...current,
        accountHolderName:
          storedDriver.bankDetails?.accountHolder || current.accountHolderName,
        bankName: storedDriver.bankDetails?.bankName || current.bankName,
        branchCode: storedDriver.bankDetails?.branchCode || current.branchCode,
      }));
      const nextStage = nextStageForDriver(storedDriver);
      setStage(nextStage);
      setShowEmailModal(nextStage === 'verify-email');
      return;
    }

    const storedCustomer = getCustomerAuthUser();
    const storedToken = getCustomerAuthToken();
    if (!storedCustomer || !storedToken) return;

    setCustomerUser(storedCustomer);
    setDriverUser(null);
    setCustomerAccountType(storedCustomer.accountType === 'BUSINESS' ? 'BUSINESS' : 'INDIVIDUAL');
    setFormData((current) => ({
      ...current,
      fullName: storedCustomer.name || current.fullName,
      email: storedCustomer.email || current.email,
    }));
    setPhone(normalizePhoneForInput(storedCustomer.phone));
    const nextStage = nextStageForCustomer(storedCustomer);
    setStage(nextStage);
    setShowEmailModal(nextStage === 'verify-email');
  }, [searchParams]);

  useEffect(() => {
    if (selectedRole !== 'customer') return;
    if (stage !== 'business-profile' && stage !== 'business-submitted') return;
    if (!isBusinessCustomer(customerUser)) return;

    let mounted = true;

    const loadBusinessProfile = async () => {
      setBusinessLoading(true);
      try {
        const profile = await fetchMyBusinessOnboarding();
        if (!mounted) return;
        applyBusinessProfile(profile);
        if (profile.verificationStatus === 'SUBMITTED') {
          setStage('business-submitted');
        } else if (profile.verificationStatus === 'APPROVED') {
          setStage('completed');
        }
      } catch {
        if (mounted && stage === 'business-submitted') {
          setStage('business-profile');
        }
        if (mounted) {
          const businessName = String(customerUser?.name ?? '').trim();
          const businessEmail = String(customerUser?.email ?? '').trim();
          setBusinessProfileForm((current) => ({
            ...current,
            legalName: current.legalName || businessName,
            tradingName: current.tradingName || businessName,
            businessEmail: current.businessEmail || businessEmail,
          }));
        }
      } finally {
        if (mounted) {
          setBusinessLoading(false);
        }
      }
    };

    void loadBusinessProfile();

    return () => {
      mounted = false;
    };
  }, [customerUser, selectedRole, stage]);

  useEffect(() => {
    if (stage !== 'business-profile') return;
    if (!signedUpBusinessName || businessProfile?.id) return;

    setBusinessProfileForm((current) => ({
      ...current,
      legalName: current.legalName || signedUpBusinessName,
      tradingName: current.tradingName || signedUpBusinessName,
      businessEmail: current.businessEmail || String(customerUser?.email ?? '').trim(),
    }));
  }, [businessProfile?.id, customerUser?.email, signedUpBusinessName, stage]);

  useEffect(() => {
    const startCamera = async () => {
      if (stage !== 'face-liveness') return;
      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraError('This browser does not support camera access for face verification.');
        return;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: 'user',
            width: { ideal: 960 },
            height: { ideal: 720 },
          },
        });

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        setCameraError('');
      } catch (error: any) {
        setCameraError(error.message || 'Could not access your camera.');
      }
    };

    const stopCamera = () => {
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };

    void startCamera();
    return stopCamera;
  }, [stage]);

  useEffect(() => {
    return () => {
      Object.values(captures).forEach((capture) => {
        if (capture?.previewUrl) {
          URL.revokeObjectURL(capture.previewUrl);
        }
      });
    };
  }, [captures]);

  useEffect(() => {
    if (!snackbar) return undefined;

    const timeoutId = window.setTimeout(() => {
      setSnackbar(null);
    }, 4500);

    return () => window.clearTimeout(timeoutId);
  }, [snackbar]);

  useEffect(() => {
    if (!showTownSearchModal || selectedRole !== 'driver') return;

    void ensureGooglePlacesLoaded().catch((error: any) => {
      const message = error.message || 'Could not load Google Places right now.';
      setErrors((current) => ({ ...current, town: message }));
      showSnackbar(message, 'error');
      setShowTownSearchModal(false);
    });
  }, [selectedRole, showTownSearchModal]);

  useEffect(() => {
    if (!showTownSearchModal || selectedRole !== 'driver') return undefined;
    if (!townSearchQuery.trim()) {
      setTownPredictions([]);
      return undefined;
    }

    let active = true;
    const timeoutId = window.setTimeout(async () => {
      try {
        await ensureGooglePlacesLoaded();
        const googleWindow = window as any;
        const autocompleteService = new googleWindow.google.maps.places.AutocompleteService();
        setTownSearchLoading(true);
        autocompleteService.getPlacePredictions(
          {
            componentRestrictions: { country: 'za' },
            input: townSearchQuery.trim(),
            types: ['(cities)'],
          },
          (predictions: any[] | null, status: string) => {
            if (!active) return;

            setTownSearchLoading(false);

            if (status !== googleWindow.google.maps.places.PlacesServiceStatus.OK || !predictions) {
              setTownPredictions([]);
              return;
            }

            const mappedPredictions = predictions
              .map((prediction) => ({
                description: prediction.description,
                placeId: prediction.place_id,
                primaryText: prediction.structured_formatting?.main_text || prediction.description,
                secondaryText: prediction.structured_formatting?.secondary_text || '',
              }));

            setTownPredictions(mappedPredictions);
          },
        );
      } catch {
        if (!active) return;
        setTownSearchLoading(false);
        setTownPredictions([]);
      }
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timeoutId);
    };
  }, [driverLocation.province, selectedRole, showTownSearchModal, townSearchQuery]);

  useEffect(() => {
    if (stage !== 'completed') return;

    navigate(selectedRole === 'driver' ? '/driver/account-ready' : '/account-ready', {
      replace: true,
    });
  }, [navigate, selectedRole, stage]);

  const showSnackbar = (message: string, tone: SnackbarTone = 'info') => {
    setSnackbar({ message, tone });
  };

  const hydrateLatestDriver = async (fallbackUser?: DriverUser | null): Promise<DriverUser> => {
    const token = getDriverAuthToken();
    const safeFallback = fallbackUser ?? driverUser ?? getDriverAuthUser();

    if (!safeFallback) {
      throw new Error('Driver session is missing. Please log in again.');
    }

    if (!token || !safeFallback.email) {
      saveDriverAuthUser(safeFallback);
      setDriverUser(safeFallback);
      return safeFallback;
    }

    try {
      const freshDriver = await fetchDriverByEmail(safeFallback.email, token);
      saveDriverAuthUser(freshDriver);
      setDriverUser(freshDriver);
      return freshDriver;
    } catch {
      saveDriverAuthUser(safeFallback);
      setDriverUser(safeFallback);
      return safeFallback;
    }
  };

  const setFieldValue =
    (field: keyof typeof formData) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((current) => ({ ...current, [field]: event.target.value }));
      if (errors[field]) {
        setErrors((current) => ({ ...current, [field]: '' }));
      }
    };

  const setLocationField =
    (field: keyof typeof driverLocation) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setDriverLocation((current) => ({ ...current, [field]: event.target.value }));
      if (field === 'province') {
        setPickedDriverTown(null);
      }
      if (errors[field]) {
        setErrors((current) => ({ ...current, [field]: '' }));
      }
    };

  const setUploadField =
    <T, K extends keyof T>(setter: React.Dispatch<React.SetStateAction<T>>, field: K) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextFile = event.target.files?.[0] || null;
      setter((current) => ({
        ...current,
        [field]: {
          file: nextFile,
          label: fileToLabel(nextFile),
        },
      } as T));
      if (errors[String(field)]) {
        setErrors((current) => ({ ...current, [String(field)]: '' }));
      }
      event.target.value = '';
    };

  const setDriverPersonalDocValue =
    (field: 'criminalIssuedOn') => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setDriverPersonalDocs((current) => ({ ...current, [field]: value }));
      if (errors[field]) {
        setErrors((current) => ({ ...current, [field]: '' }));
      }
    };

  const setDriverVehicleDetailValue =
    (field: keyof typeof driverVehicleDetails) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const rawValue = event.target.value;
      const value =
        field === 'registrationNumber'
          ? rawValue.toUpperCase().replace(/[^A-Z0-9 -]/g, '')
          : field === 'year'
            ? normalizeDigits(rawValue).slice(0, 4)
            : rawValue;

      setDriverVehicleDetails((current) => ({ ...current, [field]: value }));
      if (errors[field]) {
        setErrors((current) => ({ ...current, [field]: '' }));
      }
    };

  const setDriverVehicleFileValue =
    (field:
      | 'roadworthyIssuedOn'
      | 'licenceDiskIssuedOn'
      | 'licenceDiskValidTo'
      | 'pdpIssuedOn'
      | 'pdpValidTo') =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setDriverVehicleFiles((current) => ({ ...current, [field]: value }));
      if (errors[field]) {
        setErrors((current) => ({ ...current, [field]: '' }));
      }
    };

  const setDriverCompanyValue =
    (field: keyof typeof driverCompanyDetails) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextValue =
        field === 'companyPhone1' || field === 'companyPhone2'
          ? normalizeDigits(event.target.value)
          : event.target.value;
      setDriverCompanyDetails((current) => ({ ...current, [field]: nextValue }));
      if (errors[field]) {
        setErrors((current) => ({ ...current, [field]: '' }));
      }
    };

  const setDriverBankValue =
    (field: keyof Omit<typeof driverBankDetails, 'bankLetter'>) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextValue =
        field === 'accountNumber' || field === 'branchCode'
          ? normalizeDigits(event.target.value)
          : event.target.value;
      setDriverBankDetails((current) => ({ ...current, [field]: nextValue }));
      if (errors[field]) {
        setErrors((current) => ({ ...current, [field]: '' }));
      }
    };

  const validateAccountForm = (): boolean => {
    const nextErrors: Record<string, string> = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isBusinessCustomer = selectedRole === 'customer' && customerAccountType === 'BUSINESS';

    if (!formData.fullName.trim()) {
      nextErrors.fullName = isBusinessCustomer ? 'Business name is required.' : 'Full name is required.';
    } else if (formData.fullName.trim().length < 2) {
      nextErrors.fullName = isBusinessCustomer
        ? 'Business name must be at least 2 characters.'
        : 'Full name must be at least 2 characters.';
    }

    if (!formData.email.trim()) {
      nextErrors.email = 'Email address is required.';
    } else if (!emailRegex.test(formData.email.trim())) {
      nextErrors.email = 'Please enter a valid email address.';
    }

    if (!formData.password) {
      nextErrors.password = 'Password is required.';
    } else if (formData.password.length < 8) {
      nextErrors.password = 'Password must be at least 8 characters.';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      nextErrors.password = 'Use uppercase, lowercase, and a number in your password.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateDriverLocationForm = (): boolean => {
    const nextErrors: Record<string, string> = {};

    if (!pickedDriverTown?.town || Number.isNaN(pickedDriverTown.lat) || Number.isNaN(pickedDriverTown.lng)) {
      nextErrors.town = 'Operating town is required.';
    }

    if (!driverLocation.province.trim()) {
      nextErrors.province = 'Operating province is required.';
    }

    if (!driverLocation.country.trim()) {
      nextErrors.country = 'Operating country is required.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateDriverPersonalDocs = (): boolean => {
    const nextErrors: Record<string, string> = {};

    if (!driverPersonalDocs.idCopy.file) {
      nextErrors.idCopy = 'Upload your ID copy as a PDF.';
    }
    if (!driverPersonalDocs.criminalRecord.file) {
      nextErrors.criminalRecord = 'Upload your criminal record check as a PDF.';
    }
    if (!driverPersonalDocs.criminalIssuedOn) {
      nextErrors.criminalIssuedOn = 'Add the issue date for your criminal record check.';
    } else if (isFutureDateInputValue(driverPersonalDocs.criminalIssuedOn)) {
      nextErrors.criminalIssuedOn = 'Issue date cannot be in the future.';
    }
    if (!driverPersonalDocs.driverPhoto.file) {
      nextErrors.driverPhoto = 'Upload a clear half photo.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateDriverVehicleDetails = (): boolean => {
    const nextErrors: Record<string, string> = {};
    const year = Number(driverVehicleDetails.year);
    const maxYear = new Date().getFullYear() + 1;

    if (!driverVehicleDetails.registrationNumber.trim()) {
      nextErrors.registrationNumber = 'Registration number is required.';
    }
    if (!driverVehicleDetails.make.trim()) {
      nextErrors.make = 'Vehicle make is required.';
    }
    if (!driverVehicleDetails.model.trim()) {
      nextErrors.model = 'Vehicle model is required.';
    }
    if (!year || year < 1980 || year > maxYear) {
      nextErrors.year = 'Use a valid vehicle year.';
    }
    if (!driverVehicleDetails.color.trim()) {
      nextErrors.color = 'Vehicle color is required.';
    }
    if (
      driverVehicleDetails.vehicleType === 'BAKKIE' &&
      driverVehicleDetails.insuranceBusinessUseConfirmed !== true
    ) {
      nextErrors.insuranceBusinessUseConfirmed =
        'Confirm that the bakkie is insured for business use.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateDriverVehicleFiles = (): boolean => {
    const nextErrors: Record<string, string> = {};
    const requiredUploads: Array<keyof typeof driverVehicleFiles> = [
      'frontPhoto',
      'leftPhoto',
      'rightPhoto',
      'backPhoto',
      'roadworthy',
      'licenceDisk',
      'pdp',
      'insuranceLetter',
    ];

    requiredUploads.forEach((field) => {
      const entry = driverVehicleFiles[field];
      if (typeof entry === 'object' && entry !== null && 'file' in entry && !entry.file) {
        nextErrors[String(field)] = 'This upload is required.';
      }
    });

    if (!driverVehicleFiles.roadworthyIssuedOn) {
      nextErrors.roadworthyIssuedOn = 'Add the issue date on the roadworthy document.';
    } else if (isFutureDateInputValue(driverVehicleFiles.roadworthyIssuedOn)) {
      nextErrors.roadworthyIssuedOn = 'Issue date cannot be in the future.';
    }
    if (!driverVehicleFiles.licenceDiskIssuedOn || !driverVehicleFiles.licenceDiskValidTo) {
      nextErrors.licenceDiskIssuedOn = 'Add the issue and expiry dates on the licence disk.';
    } else if (isFutureDateInputValue(driverVehicleFiles.licenceDiskIssuedOn)) {
      nextErrors.licenceDiskIssuedOn = 'Issue date cannot be in the future.';
    }
    if (!driverVehicleFiles.pdpIssuedOn || !driverVehicleFiles.pdpValidTo) {
      nextErrors.pdpIssuedOn = 'Add the issue and expiry dates on the PDP.';
    } else if (isFutureDateInputValue(driverVehicleFiles.pdpIssuedOn)) {
      nextErrors.pdpIssuedOn = 'Issue date cannot be in the future.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateDriverAccountType = (): boolean => {
    const nextErrors: Record<string, string> = {};
    if (!driverAccountType) {
      nextErrors.accountType = 'Choose how you operate on ShiftWagon.';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateDriverCompanyDetails = (): boolean => {
    const nextErrors: Record<string, string> = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!driverCompanyDetails.companyName.trim()) {
      nextErrors.companyName = 'Company name is required.';
    }
    if (!emailRegex.test(driverCompanyDetails.companyEmail.trim())) {
      nextErrors.companyEmail = 'Use a valid company email address.';
    }
    if (normalizePhoneToE164(driverCompanyDetails.companyPhone1).length < 11) {
      nextErrors.companyPhone1 = 'Add the main company phone number with country code.';
    }
    if (
      driverCompanyDetails.companyPhone2 &&
      normalizePhoneToE164(driverCompanyDetails.companyPhone2).length < 11
    ) {
      nextErrors.companyPhone2 = 'Use a valid alternative company phone number.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateDriverBankDetails = (): boolean => {
    const nextErrors: Record<string, string> = {};

    if (!driverBankDetails.bankName.trim()) {
      nextErrors.bankName = 'Bank name is required.';
    }
    if (!driverBankDetails.accountHolderName.trim()) {
      nextErrors.accountHolderName = 'Account holder name is required.';
    }
    if (!driverBankDetails.accountNumber.trim()) {
      nextErrors.accountNumber = 'Account number is required.';
    }
    if (!driverBankDetails.bankLetter.file) {
      nextErrors.bankLetter = 'Upload your bank confirmation letter.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateBusinessProfile = (): boolean => {
    const nextErrors: Record<string, string> = {};
    if (!businessProfileForm.legalName.trim()) {
      nextErrors.legalName = 'Legal business name is required.';
    }
    if (businessProfileForm.businessEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(businessProfileForm.businessEmail.trim())) {
      nextErrors.businessEmail = 'Use a valid business email address.';
    }
    if (businessProfileForm.representativeEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(businessProfileForm.representativeEmail.trim())) {
      nextErrors.representativeEmail = 'Use a valid primary contact email address.';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateBusinessBranch = (): boolean => {
    const nextErrors: Record<string, string> = {};
    if (!businessBranchForm.branchName.trim()) {
      nextErrors.branchName = 'Branch name is required.';
    }
    if (!businessBranchForm.address.trim()) {
      nextErrors.branchAddress = 'Branch address is required.';
    }
    if (businessBranchForm.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(businessBranchForm.contactEmail.trim())) {
      nextErrors.branchContactEmail = 'Use a valid branch email address.';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleBusinessProfileSubmit = async () => {
    if (!validateBusinessProfile()) return;

    setBusinessLoading(true);
    try {
      const profile = await upsertBusinessProfile({
        legalName: businessProfileForm.legalName.trim(),
        tradingName: emptyToNull(businessProfileForm.tradingName),
        registrationNumber: emptyToNull(businessProfileForm.registrationNumber),
        vatNumber: emptyToNull(businessProfileForm.vatNumber),
        businessEmail: emptyToNull(businessProfileForm.businessEmail)?.toLowerCase() ?? null,
        businessPhone: emptyToNull(businessProfileForm.businessPhone),
        headOfficeAddress: emptyToNull(businessProfileForm.headOfficeAddress),
        representativeName: emptyToNull(businessProfileForm.representativeName),
        representativeEmail: emptyToNull(businessProfileForm.representativeEmail)?.toLowerCase() ?? null,
      });
      applyBusinessProfile(profile);
      setErrors({});
      showSnackbar('Business profile saved.', 'success');
    } catch (error: any) {
      const message = error.message || 'Could not save business profile.';
      setErrors((current) => ({ ...current, submit: message }));
      showSnackbar(message, 'error');
    } finally {
      setBusinessLoading(false);
    }
  };

  const handleBusinessBranchSubmit = async () => {
    if (!validateBusinessBranch()) return;

    setBusinessLoading(true);
    try {
      const payload = {
        branchName: businessBranchForm.branchName.trim(),
        address: businessBranchForm.address.trim(),
        contactEmail: emptyToNull(businessBranchForm.contactEmail)?.toLowerCase() ?? null,
        contactPhone: emptyToNull(businessBranchForm.contactPhone),
        defaultBranch: businessBranchForm.defaultBranch,
      };
      const profile = businessBranchForm.id
        ? await updateBusinessBranch(businessBranchForm.id, payload)
        : await addBusinessBranch(payload);
      applyBusinessProfile(profile);
      setShowBusinessBranchForm(false);
      setErrors({});
      showSnackbar('Business branch saved.', 'success');
    } catch (error: any) {
      const message = error.message || 'Could not save business branch.';
      setErrors((current) => ({ ...current, submit: message }));
      showSnackbar(message, 'error');
    } finally {
      setBusinessLoading(false);
    }
  };

  const handleDeleteBusinessBranch = async (branchId: string) => {
    setBusinessLoading(true);
    try {
      const profile = await deleteBusinessBranch(branchId);
      applyBusinessProfile(profile);
      setBusinessBranchForm({
        id: '',
        branchName: '',
        address: '',
        contactEmail: '',
        contactPhone: '',
        defaultBranch: false,
      });
      setShowBusinessBranchForm(false);
      showSnackbar('Business branch removed.', 'success');
    } catch (error: any) {
      const message = error.message || 'Could not remove business branch.';
      setErrors((current) => ({ ...current, submit: message }));
      showSnackbar(message, 'error');
    } finally {
      setBusinessLoading(false);
    }
  };

  const handleSubmitBusinessOnboarding = async () => {
    if (!businessProfile?.id) {
      setErrors({ submit: 'Save the business profile before submitting.' });
      return;
    }

    setBusinessLoading(true);
    try {
      const profile = await submitBusinessOnboarding();
      applyBusinessProfile(profile);
      setStage('business-submitted');
      showSnackbar('Business profile submitted for review.', 'success');
    } catch (error: any) {
      const message = error.message || 'Could not submit business onboarding.';
      setErrors((current) => ({ ...current, submit: message }));
      showSnackbar(message, 'error');
    } finally {
      setBusinessLoading(false);
    }
  };

  const handleAccountSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateAccountForm()) return;

    if (selectedRole === 'driver') {
      setErrors({});
      setStage('driver-location');
      return;
    }

    setLoading(true);
    try {
      const response = await customerSignUp({
        name: formData.fullName.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        signUpMethod: 'manual',
        role: ['ROLE_USER'],
        accountType: customerAccountType,
      });

      if (!response.success) {
        throw new Error(response.message || 'Could not create account.');
      }

      clearDriverAuthFlow();
      if (response.userResponse) {
        setCustomerAccountType(response.userResponse.accountType === 'BUSINESS' ? 'BUSINESS' : customerAccountType);
        setCustomerUser(response.userResponse);
      }

      setErrors({});
      setEmailCode('');
      setEmailError('');
      setShowEmailModal(true);
      showSnackbar('Account created. Check your email for the verification code.', 'success');
    } catch (error: any) {
      const message = error.message || 'Could not create account right now.';
      setErrors((current) => ({
        ...current,
        submit: message,
      }));
      showSnackbar(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDriverTownSelection = async (prediction: DriverTownPrediction) => {
    try {
      await ensureGooglePlacesLoaded();
      const googleWindow = window as any;
      const placesService = new googleWindow.google.maps.places.PlacesService(
        document.createElement('div'),
      );

      setTownSearchLoading(true);

      placesService.getDetails(
        {
          fields: ['address_components', 'formatted_address', 'geometry', 'name'],
          placeId: prediction.placeId,
        },
        (place: any, status: string) => {
          setTownSearchLoading(false);

          if (status !== googleWindow.google.maps.places.PlacesServiceStatus.OK || !place) {
            const message = 'Could not load the selected town details. Please try another result.';
            setErrors((current) => ({ ...current, town: message }));
            showSnackbar(message, 'error');
            return;
          }

          const town = extractTownFromPlace(place);
          const province = extractProvinceFromPlace(place) || driverLocation.province;
          const lat = place.geometry?.location?.lat?.();
          const lng = place.geometry?.location?.lng?.();

          if (!town || typeof lat !== 'number' || typeof lng !== 'number') {
            const message = 'This result is missing location coordinates. Please pick another town.';
            setErrors((current) => ({ ...current, town: message }));
            showSnackbar(message, 'error');
            return;
          }

          setPickedDriverTown({
            address: place.formatted_address || prediction.description,
            lat,
            lng,
            province,
            town,
          });
          setDriverLocation((current) => ({
            ...current,
            province,
          }));
          setErrors((current) => ({ ...current, town: '', province: '' }));
          setTownSearchQuery('');
          setTownPredictions([]);
          setShowTownSearchModal(false);
        },
      );
    } catch (error: any) {
      setTownSearchLoading(false);
      const message = error.message || 'Could not load the selected town details.';
      setErrors((current) => ({ ...current, town: message }));
      showSnackbar(message, 'error');
    }
  };

  const handleDriverLocationSubmit = async () => {
    if (!validateDriverLocationForm()) return;

    setLocationLoading(true);
    try {
      const response = await driverSignUp({
        email: formData.email.trim().toLowerCase(),
        home_base_country: driverLocation.country.trim(),
        home_base_lat: pickedDriverTown!.lat,
        home_base_lng: pickedDriverTown!.lng,
        home_base_province: driverLocation.province.trim(),
        home_base_town: pickedDriverTown!.town,
        name: formData.fullName.trim(),
        password: formData.password,
        roles: ['ROLE_DRIVER'],
        sign_up_method: 'manual',
      });

      if (!response.success && !/driver created successfully/i.test(response.message || '')) {
        throw new Error(response.message || 'Could not create driver account.');
      }

      clearCustomerAuthFlow();
      if (response.driverResponse) {
        setDriverUser(response.driverResponse);
      }

      setErrors({});
      setStage('verify-email');
      setEmailCode('');
      setEmailError('');
      setShowEmailModal(true);
      showSnackbar('Driver account created. Check your email for the verification code.', 'success');
    } catch (error: any) {
      const message = error.message || 'Could not create driver account right now.';
      setErrors((current) => ({
        ...current,
        submit: message,
      }));
      showSnackbar(message, 'error');
    } finally {
      setLocationLoading(false);
    }
  };

  const handleVerifyEmail = async () => {
    if (emailCode.trim().length !== 6) {
      setEmailError('Enter the 6-digit code sent to your email.');
      return;
    }

    setEmailLoading(true);
    try {
      if (selectedRole === 'driver') {
        const response = await verifyDriverEmail(formData.email.trim().toLowerCase(), emailCode.trim());
        const accessToken = response.accessToken ?? response.token;

        if (!response.success || !response.driverResponse || !accessToken || !response.refreshToken) {
          throw new Error(response.message || 'Email verification failed.');
        }

        clearCustomerAuthFlow();
        saveDriverAuthTokens({
          accessToken,
          refreshToken: response.refreshToken,
          accessTokenExpiresAt: response.accessTokenExpiresAt,
          refreshTokenExpiresAt: response.refreshTokenExpiresAt,
        });
        saveDriverAuthUser(response.driverResponse);
        setDriverUser(response.driverResponse);
        setShowEmailModal(false);
        setStage(nextStageForDriver(response.driverResponse));
        showSnackbar('Email verified. Continue with phone verification.', 'success');
        return;
      }

      const response = await verifyCustomerEmail(formData.email.trim().toLowerCase(), emailCode.trim());
      const accessToken = response.accessToken ?? response.token;

      if (!response.success || !response.userResponse || !accessToken || !response.refreshToken) {
        throw new Error(response.message || 'Email verification failed.');
      }

      clearDriverAuthFlow();
      saveCustomerAuthTokens({
        accessToken,
        refreshToken: response.refreshToken,
        accessTokenExpiresAt: response.accessTokenExpiresAt,
        refreshTokenExpiresAt: response.refreshTokenExpiresAt,
      });
      saveCustomerAuthUser(response.userResponse);
      setCustomerAccountType(response.userResponse.accountType === 'BUSINESS' ? 'BUSINESS' : 'INDIVIDUAL');
      setCustomerUser(response.userResponse);
      setShowEmailModal(false);
      const nextCustomerStage = nextStageForCustomer(response.userResponse);
      setStage(nextCustomerStage);
      showSnackbar(
        response.userResponse.accountType === 'BUSINESS'
          ? 'Email verified. Continue with business onboarding.'
          : 'Email verified. Continue with face verification.',
        'success',
      );
    } catch (error: any) {
      const message = error.message || 'Email verification failed.';
      setEmailError(message);
      showSnackbar(message, 'error');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleResendEmail = async () => {
    setEmailLoading(true);
    try {
      if (selectedRole === 'driver') {
        await resendDriverEmailOtp(formData.email.trim().toLowerCase());
      } else {
        await resendCustomerEmailOtp(formData.email.trim().toLowerCase());
      }
      setEmailError('');
      showSnackbar('A new email verification code has been sent.', 'success');
    } catch (error: any) {
      const message = error.message || 'Could not resend the code.';
      setEmailError(message);
      showSnackbar(message, 'error');
    } finally {
      setEmailLoading(false);
    }
  };

  const captureCurrentPose = async () => {
    if (!videoRef.current || !canvasRef.current || capturing) return;

    setCapturing(true);
    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth || 960;
      canvas.height = video.videoHeight || 720;

      const context = canvas.getContext('2d');
      if (!context) {
        throw new Error('Could not capture image from the camera.');
      }

      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((nextBlob) => {
          if (nextBlob) resolve(nextBlob);
          else reject(new Error('Could not prepare captured image.'));
        }, 'image/jpeg', 0.9);
      });

      setCaptures((current) => {
        const previous = current[currentLivenessStep.key];
        if (previous?.previewUrl) {
          URL.revokeObjectURL(previous.previewUrl);
        }

        const next = {
          ...current,
          [currentLivenessStep.key]: {
            blob,
            previewUrl: URL.createObjectURL(blob),
          },
        };

        const nextIndex = LIVENESS_STEPS.findIndex((stepConfig) => !next[stepConfig.key]);
        if (nextIndex >= 0) {
          setCurrentLivenessIndex(nextIndex);
        }

        return next;
      });
    } catch (error: any) {
      setCameraError(error.message || 'Could not capture your face image.');
    } finally {
      setCapturing(false);
    }
  };

  const uploadLivenessPack = async () => {
    if (!activeUser?.id || !livenessComplete) return;

    setUploadingLiveness(true);
    try {
      let latestUser = activeUser;

      for (let index = 0; index < LIVENESS_STEPS.length; index += 1) {
        const stepConfig = LIVENESS_STEPS[index];
        const capture = captures[stepConfig.key];

        if (!capture?.blob) {
          throw new Error('Complete all three face poses before continuing.');
        }

        if (selectedRole === 'driver') {
          latestUser = await uploadDriverFaceLiveness(
            activeUser.id,
            stepConfig.key,
            (index + 1) as 1 | 2 | 3,
            capture.blob,
          );
        } else {
          latestUser = await uploadCustomerFaceLiveness(
            activeUser.id,
            stepConfig.key,
            (index + 1) as 1 | 2 | 3,
            capture.blob,
          );
        }
      }

      if (selectedRole === 'driver') {
        const resolvedDriver = await hydrateLatestDriver(latestUser as DriverUser);
        setStage(nextStageForDriver(resolvedDriver));
        showSnackbar('Face verification uploaded. Continue with your personal documents.', 'success');
      } else {
        setCustomerUser(latestUser as CustomerUser);
        saveCustomerAuthUser(latestUser as CustomerUser);
        setStage(nextStageForCustomer(latestUser as CustomerUser));
        showSnackbar('Face verification uploaded. Continue with phone verification.', 'success');
      }
    } catch (error: any) {
      const message = error.message || 'Could not upload face verification images.';
      setCameraError(message);
      showSnackbar(message, 'error');
    } finally {
      setUploadingLiveness(false);
    }
  };

  const handleSendPhoneCode = async () => {
    if (!activeUser?.id) return;

    const normalizedPhone = normalizePhoneToE164(phone);

    if (!/^\+\d{7,15}$/.test(normalizedPhone)) {
      setPhoneError('Use a valid phone number with country code, for example 27711234567.');
      return;
    }

    setPhoneLoading(true);
    try {
      if (selectedRole === 'driver') {
        await sendDriverPhoneOtp(activeUser.id, normalizedPhone);
      } else {
        await sendCustomerPhoneOtp(activeUser.id, normalizedPhone);
      }
      setPhoneCode('');
      setPhoneError('');
      setShowPhoneModal(true);
      showSnackbar('Phone verification code sent.', 'success');
    } catch (error: any) {
      const message = error.message || 'Could not send the phone verification code.';
      setPhoneError(message);
      showSnackbar(message, 'error');
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleVerifyPhone = async () => {
    if (!activeUser?.id) return;

    const normalizedPhone = normalizePhoneToE164(phone);

    if (phoneCode.trim().length !== 6) {
      setPhoneError('Enter the 6-digit code sent to your phone.');
      return;
    }

    setPhoneLoading(true);
    try {
      if (selectedRole === 'driver') {
        const user = await verifyDriverPhoneOtp(activeUser.id, normalizedPhone, phoneCode.trim());
        const resolvedDriver = await hydrateLatestDriver(user);
        setShowPhoneModal(false);
        setStage(nextStageForDriver(resolvedDriver));
        showSnackbar('Phone number verified. Continue with face verification.', 'success');
      } else {
        const user = await verifyCustomerPhoneOtp(activeUser.id, normalizedPhone, phoneCode.trim());
        setCustomerUser(user);
        saveCustomerAuthUser(user);
        setShowPhoneModal(false);
        setStage(nextStageForCustomer(user));
        showSnackbar('Phone number verified successfully.', 'success');
      }
    } catch (error: any) {
      const message = error.message || 'Could not verify your phone number.';
      setPhoneError(message);
      showSnackbar(message, 'error');
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleDriverPersonalDocsSubmit = async () => {
    if (!driverUser?.id || !validateDriverPersonalDocs()) return;

    setDriverStageLoading(true);
    try {
      let latestUser = driverUser;
      latestUser = await uploadDriverIdCopy(driverUser.id, driverPersonalDocs.idCopy.file!);
      latestUser = await uploadDriverCriminalRecord(
        driverUser.id,
        driverPersonalDocs.criminalRecord.file!,
        formatDateForApi(driverPersonalDocs.criminalIssuedOn),
      );
      latestUser = await uploadDriverPhoto(driverUser.id, driverPersonalDocs.driverPhoto.file!);
      latestUser = await completeDriverPersonalDocs(driverUser.id);
      const resolvedDriver = await hydrateLatestDriver(latestUser);
      setStage(nextStageForDriver(resolvedDriver));
      setErrors({});
      showSnackbar('Personal documents saved. Continue with vehicle details.', 'success');
    } catch (error: any) {
      const message = error.message || 'Could not save personal documents.';
      setErrors((current) => ({ ...current, submit: message }));
      showSnackbar(message, 'error');
    } finally {
      setDriverStageLoading(false);
    }
  };

  const handleDriverVehicleDetailsSubmit = async () => {
    if (!driverUser?.id || !validateDriverVehicleDetails()) return;

    setDriverStageLoading(true);
    try {
      const payload: DriverVehicleDetailsRequest = {
        make: driverVehicleDetails.make.trim(),
        model: driverVehicleDetails.model.trim(),
        registration_number: driverVehicleDetails.registrationNumber.trim(),
        vehicle_color: driverVehicleDetails.color.trim(),
        vehicle_type: driverVehicleDetails.vehicleType,
        year: Number(driverVehicleDetails.year),
        ...(driverVehicleDetails.vehicleType === 'BAKKIE'
          ? {
              insurance_business_use_confirmed:
                driverVehicleDetails.insuranceBusinessUseConfirmed,
            }
          : {}),
      };

      const latestUser = await submitDriverVehicleDetails(driverUser.id, payload);
      const resolvedDriver = await hydrateLatestDriver(latestUser);
      setStage(nextStageForDriver(resolvedDriver));
      setErrors({});
      showSnackbar('Vehicle details saved. Upload vehicle photos and documents next.', 'success');
    } catch (error: any) {
      const message = error.message || 'Could not save vehicle details.';
      setErrors((current) => ({ ...current, submit: message }));
      showSnackbar(message, 'error');
    } finally {
      setDriverStageLoading(false);
    }
  };

  const handleDriverVehicleFilesSubmit = async () => {
    if (!driverUser?.id || !validateDriverVehicleFiles()) return;

    setDriverStageLoading(true);
    try {
      let latestUser = driverUser;
      latestUser = await uploadDriverVehiclePhoto(driverUser.id, 'FRONT', driverVehicleFiles.frontPhoto.file!);
      latestUser = await uploadDriverVehiclePhoto(driverUser.id, 'LEFT', driverVehicleFiles.leftPhoto.file!);
      latestUser = await uploadDriverVehiclePhoto(driverUser.id, 'RIGHT', driverVehicleFiles.rightPhoto.file!);
      latestUser = await uploadDriverVehiclePhoto(driverUser.id, 'BACK', driverVehicleFiles.backPhoto.file!);
      latestUser = await uploadDriverRoadworthy(
        driverUser.id,
        driverVehicleFiles.roadworthy.file!,
        formatDateForApi(driverVehicleFiles.roadworthyIssuedOn),
        formatDateForApi(driverVehicleFiles.roadworthyIssuedOn),
      );
      latestUser = await uploadDriverLicenceDisk(
        driverUser.id,
        driverVehicleFiles.licenceDisk.file!,
        formatDateForApi(driverVehicleFiles.licenceDiskIssuedOn),
        formatDateForApi(driverVehicleFiles.licenceDiskValidTo),
      );
      latestUser = await uploadDriverPdp(
        driverUser.id,
        driverVehicleFiles.pdp.file!,
        formatDateForApi(driverVehicleFiles.pdpIssuedOn),
        formatDateForApi(driverVehicleFiles.pdpValidTo),
      );
      latestUser = await uploadDriverInsuranceLetter(driverUser.id, driverVehicleFiles.insuranceLetter.file!);
      latestUser = await completeDriverVehicleInformation(driverUser.id);
      const resolvedDriver = await hydrateLatestDriver(latestUser);
      setStage(nextStageForDriver(resolvedDriver));
      setErrors({});
      showSnackbar('Vehicle photos and documents saved. Choose your account type next.', 'success');
    } catch (error: any) {
      const message = error.message || 'Could not save vehicle documents.';
      setErrors((current) => ({ ...current, submit: message }));
      showSnackbar(message, 'error');
    } finally {
      setDriverStageLoading(false);
    }
  };

  const handleDriverAccountTypeSubmit = async () => {
    if (!driverUser?.id || !validateDriverAccountType()) return;

    setDriverStageLoading(true);
    try {
      const latestUser = await saveDriverAccountType(driverUser.id, driverAccountType!);
      await hydrateLatestDriver(latestUser);
      setStage(driverAccountType === 'COMPANY_DRIVER' ? 'company-details' : 'bank-details');
      setErrors({});
      showSnackbar(
        driverAccountType === 'COMPANY_DRIVER'
          ? 'Account type saved. Add company details next.'
          : 'Account type saved. Add banking details next.',
        'success',
      );
    } catch (error: any) {
      const message = error.message || 'Could not save account type.';
      setErrors((current) => ({ ...current, submit: message }));
      showSnackbar(message, 'error');
    } finally {
      setDriverStageLoading(false);
    }
  };

  const handleDriverCompanyDetailsSubmit = async () => {
    if (!driverUser?.id || !validateDriverCompanyDetails()) return;

    setDriverStageLoading(true);
    try {
      const payload: DriverCompanyDetailsRequest = {
        companyEmail: driverCompanyDetails.companyEmail.trim().toLowerCase(),
        companyName: driverCompanyDetails.companyName.trim(),
        companyPhone1E164: normalizePhoneToE164(driverCompanyDetails.companyPhone1),
        ...(driverCompanyDetails.companyPhone2
          ? { companyPhone2E164: normalizePhoneToE164(driverCompanyDetails.companyPhone2) }
          : {}),
      };
      const latestUser = await submitDriverCompanyDetails(driverUser.id, payload);
      await hydrateLatestDriver(latestUser);
      setStage('submitted');
      setErrors({});
      showSnackbar('Company details saved. Submit your onboarding next.', 'success');
    } catch (error: any) {
      const message = error.message || 'Could not save company details.';
      setErrors((current) => ({ ...current, submit: message }));
      showSnackbar(message, 'error');
    } finally {
      setDriverStageLoading(false);
    }
  };

  const handleDriverBankDetailsSubmit = async () => {
    if (!driverUser?.id || !validateDriverBankDetails()) return;

    setDriverStageLoading(true);
    try {
      let latestUser = await uploadDriverBankLetter(driverUser.id, driverBankDetails.bankLetter.file!);
      const payload: DriverBankDetailsRequest = {
        accountHolderName: driverBankDetails.accountHolderName.trim(),
        accountNumber: driverBankDetails.accountNumber.trim(),
        bankName: driverBankDetails.bankName.trim(),
        ...(driverBankDetails.branchCode.trim() ? { branchCode: driverBankDetails.branchCode.trim() } : {}),
      };
      latestUser = await submitDriverBankDetails(driverUser.id, payload);
      await hydrateLatestDriver(latestUser);
      setStage('submitted');
      setErrors({});
      showSnackbar('Banking details saved. Submit your onboarding next.', 'success');
    } catch (error: any) {
      const message = error.message || 'Could not save banking details.';
      setErrors((current) => ({ ...current, submit: message }));
      showSnackbar(message, 'error');
    } finally {
      setDriverStageLoading(false);
    }
  };

  const handleDriverOnboardingSubmit = async () => {
    if (!driverUser?.id) return;

    setDriverStageLoading(true);
    try {
      const latestUser = await submitDriverOnboarding(driverUser.id);
      await hydrateLatestDriver(latestUser);
      setStage('completed');
      showSnackbar('Driver onboarding submitted successfully.', 'success');
    } catch (error: any) {
      const message = error.message || 'Could not submit driver onboarding.';
      setErrors((current) => ({ ...current, submit: message }));
      showSnackbar(message, 'error');
    } finally {
      setDriverStageLoading(false);
    }
  };

  const resetFlow = () => {
    clearCustomerAuthFlow();
    clearDriverAuthFlow();
    setCustomerUser(null);
    setDriverUser(null);
    setCustomerAccountType('INDIVIDUAL');
    setBusinessProfile(null);
    setBusinessProfileForm({
      legalName: '',
      tradingName: '',
      registrationNumber: '',
      vatNumber: '',
      businessEmail: '',
      businessPhone: '',
      headOfficeAddress: '',
      representativeName: '',
      representativeEmail: '',
    });
    setBusinessBranchForm({
      id: '',
      branchName: '',
      address: '',
      contactEmail: '',
      contactPhone: '',
      defaultBranch: false,
    });
    setShowBusinessBranchForm(false);
    setStage('account');
    setShowEmailModal(false);
    setShowPhoneModal(false);
    setErrors({});
    setEmailCode('');
    setEmailError('');
    setPhone('');
    setPhoneCode('');
    setPhoneError('');
    setCaptures({});
    setCurrentLivenessIndex(0);
    setCameraError('');
    setPickedDriverTown(null);
    setTownSearchQuery('');
    setTownPredictions([]);
    setShowTownSearchModal(false);
    setDriverPersonalDocs({
      criminalIssuedOn: '',
      criminalRecord: { file: null, label: '' },
      driverPhoto: { file: null, label: '' },
      idCopy: { file: null, label: '' },
    });
    setDriverVehicleDetails({
      color: '',
      insuranceBusinessUseConfirmed: null,
      make: '',
      model: '',
      registrationNumber: '',
      vehicleType: 'TRUCK_4_TON',
      year: '',
    });
    setDriverVehicleFiles({
      backPhoto: { file: null, label: '' },
      frontPhoto: { file: null, label: '' },
      insuranceLetter: { file: null, label: '' },
      leftPhoto: { file: null, label: '' },
      licenceDisk: { file: null, label: '' },
      licenceDiskIssuedOn: '',
      licenceDiskValidTo: '',
      pdp: { file: null, label: '' },
      pdpIssuedOn: '',
      pdpValidTo: '',
      rightPhoto: { file: null, label: '' },
      roadworthy: { file: null, label: '' },
      roadworthyIssuedOn: '',
    });
    setDriverAccountType(null);
    setDriverCompanyDetails({
      companyEmail: '',
      companyName: '',
      companyPhone1: '',
      companyPhone2: '',
    });
    setDriverBankDetails({
      accountHolderName: '',
      accountNumber: '',
      bankLetter: { file: null, label: '' },
      bankName: '',
      branchCode: '',
    });
  };

  const handleRoleSwitch = (role: AppRole) => {
    setSelectedRole(role);
    setStage('account');
    setCustomerUser(null);
    setDriverUser(null);
    setCustomerAccountType('INDIVIDUAL');
    setBusinessProfile(null);
    setBusinessProfileForm({
      legalName: '',
      tradingName: '',
      registrationNumber: '',
      vatNumber: '',
      businessEmail: '',
      businessPhone: '',
      headOfficeAddress: '',
      representativeName: '',
      representativeEmail: '',
    });
    setBusinessBranchForm({
      id: '',
      branchName: '',
      address: '',
      contactEmail: '',
      contactPhone: '',
      defaultBranch: false,
    });
    setShowBusinessBranchForm(false);
    setErrors({});
    setEmailError('');
    setPhoneError('');
    setShowEmailModal(false);
    setShowPhoneModal(false);
    setCaptures({});
    setCurrentLivenessIndex(0);
    setPickedDriverTown(null);
    setTownSearchQuery('');
    setTownPredictions([]);
    setShowTownSearchModal(false);
    navigate(`/register?role=${role}`, { replace: true });
  };

  const renderRoleCards = () => (
    <fieldset className="register-role-group">
      <legend className="register-section-title">Choose your role</legend>
      <div className="register-role-grid">
        {roles.map((role) => {
          const isActive = role.id === selectedRole;
          const isDisabled = !role.available;
          return (
            <button
              className={`register-role-card${isActive ? ' register-role-card-active' : ''}${
                isDisabled ? ' register-role-card-disabled' : ''
              }`}
                disabled={isDisabled}
                key={role.id}
                onClick={() => {
                  if (role.id === 'company') {
                    navigate('/register?role=company');
                    return;
                  }

                  if (role.id === 'customer' || role.id === 'driver') {
                    handleRoleSwitch(role.id);
                  }
                }}
              type="button"
            >
              <span className="register-role-icon">{role.badge}</span>
              <span className="register-role-text">
                <span className="register-role-title">{role.title}</span>
                <span className="register-role-description">{role.description}</span>
                <span className="register-role-note">{role.note}</span>
              </span>
            </button>
          );
        })}
      </div>
    </fieldset>
  );

  const renderDriverAccessMessage = () => (
    <div className="register-flow-panel register-flow-panel-success">
      <div className="register-status-badge">Partner Onboarding</div>
      <h2 className="register-form-title">Driver onboarding is currently contact-led.</h2>
      <p className="register-form-copy">
        ShiftWagon driver onboarding is currently available through approved transport partners
        and selected operators. If you are an independent driver, fleet owner, or logistics business
        interested in joining the ShiftWagon network, contact our team and we will guide you through
        the next onboarding step.
      </p>

      <div className="register-selected-town-card" style={{ width: '100%' }}>
        <div className="register-selected-town-header">
          <strong>What happens next</strong>
        </div>
        <div className="register-selected-town-meta">
          1. Tell us about your transport operation, vehicle capacity, and service area
        </div>
        <div className="register-selected-town-meta">
          2. Our team will confirm the right onboarding route for your profile or company
        </div>
        <div className="register-selected-town-meta">
          3. Approved drivers and partner teams receive the correct app access directly from ShiftWagon
        </div>
      </div>

      <div className="register-flow-actions">
        <Link className="register-submit register-submit-inline" to={DRIVER_PARTNER_CONTACT_PATH}>
          Contact ShiftWagon
        </Link>
        <button className="register-secondary-button" onClick={() => handleRoleSwitch('customer')} type="button">
          Register as customer
        </button>
      </div>
    </div>
  );

  const renderDriverProgress = () => {
    if (selectedRole !== 'driver') return null;

    const visibleProgress =
      stage === 'account' || stage === 'driver-location' || stage === 'completed' || stage === 'submitted'
        ? DRIVER_PROGRESS_STEPS
        : DRIVER_PROGRESS_STEPS.slice(2);

    return (
      <div className="register-progress-strip" aria-label="Driver onboarding progress">
        {visibleProgress.map((item) => {
          const index = DRIVER_PROGRESS_STEPS.findIndex((stepItem) => stepItem.stage === item.stage);
          const isActive = item.stage === stage;
          const isComplete = driverProgressIndex > index;
          return (
            <div
              className={`register-progress-pill${isActive ? ' register-progress-pill-active' : ''}${isComplete ? ' register-progress-pill-complete' : ''}`}
              key={item.stage}
            >
              <span className="register-progress-dot" />
              <span>{item.label}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderFilePicker = (
    label: string,
    accept: string,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
    selectedLabel: string,
    errorKey: string,
    hint?: string,
  ) => (
    <div className="register-field">
      <label className="register-label">{label}</label>
      <label className="register-file-picker">
        <span className={selectedLabel ? 'register-file-name' : 'register-file-placeholder'}>
          {selectedLabel || 'Choose file'}
        </span>
        <span className="register-picker-action">Upload</span>
        <input accept={accept} className="register-file-input" onChange={onChange} type="file" />
      </label>
      {hint ? <div className="register-field-hint">{hint}</div> : null}
      {errors[errorKey] ? <div className="register-inline-error">{errors[errorKey]}</div> : null}
    </div>
  );

  const renderDriverPersonalDocs = () => (
    <div className="register-flow-panel">
      <div className="register-form-badge">Personal Documents</div>
      <h2 className="register-form-title">Upload your personal documents</h2>
      <p className="register-form-copy">
        Match the driver app flow by adding your ID copy, criminal record check, and a clear half photo before we move to vehicle details.
      </p>

      <div className="register-section-card">
        <strong>What we need in this step</strong>
        <div className="register-feature-list">
          <div className="register-feature-item">
            <span className="register-feature-icon">ID</span>
            <div>
              <div className="register-feature-title">ID copy</div>
              <div className="register-feature-copy">Required as a PDF so the document stays readable.</div>
            </div>
          </div>
          <div className="register-feature-item">
            <span className="register-feature-icon">CR</span>
            <div>
              <div className="register-feature-title">Criminal record check</div>
              <div className="register-feature-copy">Upload the PDF and add the issue date shown on the document.</div>
            </div>
          </div>
          <div className="register-feature-item">
            <span className="register-feature-icon">PH</span>
            <div>
              <div className="register-feature-title">Half photo</div>
              <div className="register-feature-copy">Use a clear image of your face against a simple background.</div>
            </div>
          </div>
        </div>
      </div>

      <div className="register-section-card">
        <strong>Required before you continue</strong>
        <div className="register-checklist-item">
          <span className={`register-checklist-dot${driverPersonalDocs.idCopy.file ? ' register-checklist-dot-complete' : ''}`} />
          <span>ID copy PDF</span>
        </div>
        <div className="register-checklist-item">
          <span className={`register-checklist-dot${driverPersonalDocs.criminalRecord.file ? ' register-checklist-dot-complete' : ''}`} />
          <span>Criminal record check PDF</span>
        </div>
        <div className="register-checklist-item">
          <span className={`register-checklist-dot${driverPersonalDocs.criminalIssuedOn ? ' register-checklist-dot-complete' : ''}`} />
          <span>Criminal record issue date</span>
        </div>
        <div className="register-checklist-item">
          <span className={`register-checklist-dot${driverPersonalDocs.driverPhoto.file ? ' register-checklist-dot-complete' : ''}`} />
          <span>Half photo image</span>
        </div>
      </div>

      <div className="register-field-group">
        {renderFilePicker(
          'ID Copy *',
          'application/pdf',
          setUploadField(setDriverPersonalDocs, 'idCopy'),
          driverPersonalDocs.idCopy.label,
          'idCopy',
          'Upload a PDF version of your government-issued ID copy.',
        )}
        {renderFilePicker(
          'Criminal Record Check *',
          'application/pdf',
          setUploadField(setDriverPersonalDocs, 'criminalRecord'),
          driverPersonalDocs.criminalRecord.label,
          'criminalRecord',
          'Upload the latest police clearance or criminal record check as a PDF.',
        )}
        <div className="register-field">
          <label className="register-label" htmlFor="criminalIssuedOn">
            Criminal Record Issue Date <span className="register-required">*</span>
          </label>
          <input
            className="register-input"
            id="criminalIssuedOn"
            max={todayDateInputValue}
            onChange={setDriverPersonalDocValue('criminalIssuedOn')}
            type="date"
            value={driverPersonalDocs.criminalIssuedOn}
          />
          <div className="register-field-hint">Use the issue date printed on the document.</div>
          {errors.criminalIssuedOn ? (
            <div className="register-inline-error">{errors.criminalIssuedOn}</div>
          ) : null}
        </div>
        {renderFilePicker(
          'Half Photo *',
          'image/*',
          setUploadField(setDriverPersonalDocs, 'driverPhoto'),
          driverPersonalDocs.driverPhoto.label,
          'driverPhoto',
          'Use a clear photo of your face against a clean background.',
        )}
      </div>

      {errors.submit ? <div className="register-inline-error">{errors.submit}</div> : null}

      <div className="register-flow-actions">
        <button className="register-secondary-button" onClick={() => setStage('face-liveness')} type="button">
          Back
        </button>
        <button
          className="register-submit register-submit-inline"
          disabled={driverStageLoading}
          onClick={handleDriverPersonalDocsSubmit}
          type="button"
        >
          {driverStageLoading ? 'Saving documents...' : 'Save Personal Documents'}
        </button>
      </div>
    </div>
  );

  const renderDriverVehicleDetails = () => (
    <div className="register-flow-panel">
      <div className="register-form-badge">Vehicle Details</div>
      <h2 className="register-form-title">Tell us about your vehicle</h2>
      <p className="register-form-copy">
        Add the same core vehicle details the driver app collects so we can verify the vehicle before assignments start.
      </p>

      <div className="register-field-group">
        <div className="register-field">
          <label className="register-label" htmlFor="vehicleType">
            Vehicle Type
          </label>
          <select
            className="register-input register-select"
            id="vehicleType"
            onChange={setDriverVehicleDetailValue('vehicleType')}
            value={driverVehicleDetails.vehicleType}
          >
            {DRIVER_VEHICLE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div className="register-field-hint">
            {DRIVER_VEHICLE_OPTIONS.find((option) => option.value === driverVehicleDetails.vehicleType)?.hint}
          </div>
        </div>
        <div className="register-field">
          <label className="register-label" htmlFor="registrationNumber">
            Registration Number
          </label>
          <input
            className="register-input"
            id="registrationNumber"
            onChange={setDriverVehicleDetailValue('registrationNumber')}
            placeholder="e.g. KFJ 701 EC"
            value={driverVehicleDetails.registrationNumber}
          />
          {errors.registrationNumber ? <div className="register-inline-error">{errors.registrationNumber}</div> : null}
        </div>
        <div className="register-field-inline">
          <div className="register-field">
            <label className="register-label" htmlFor="vehicleMake">
              Make
            </label>
            <input
              className="register-input"
              id="vehicleMake"
              onChange={setDriverVehicleDetailValue('make')}
              placeholder="Toyota"
              value={driverVehicleDetails.make}
            />
            {errors.make ? <div className="register-inline-error">{errors.make}</div> : null}
          </div>
          <div className="register-field">
            <label className="register-label" htmlFor="vehicleModel">
              Model
            </label>
            <input
              className="register-input"
              id="vehicleModel"
              onChange={setDriverVehicleDetailValue('model')}
              placeholder="Hilux"
              value={driverVehicleDetails.model}
            />
            {errors.model ? <div className="register-inline-error">{errors.model}</div> : null}
          </div>
        </div>
        <div className="register-field-inline">
          <div className="register-field">
            <label className="register-label" htmlFor="vehicleYear">
              Year
            </label>
            <input
              className="register-input"
              id="vehicleYear"
              inputMode="numeric"
              onChange={setDriverVehicleDetailValue('year')}
              placeholder="2022"
              value={driverVehicleDetails.year}
            />
            {errors.year ? <div className="register-inline-error">{errors.year}</div> : null}
          </div>
          <div className="register-field">
            <label className="register-label" htmlFor="vehicleColor">
              Color
            </label>
            <input
              className="register-input"
              id="vehicleColor"
              onChange={setDriverVehicleDetailValue('color')}
              placeholder="White"
              value={driverVehicleDetails.color}
            />
            {errors.color ? <div className="register-inline-error">{errors.color}</div> : null}
          </div>
        </div>

        {driverVehicleDetails.vehicleType === 'BAKKIE' ? (
          <div className="register-choice-card">
            <strong>Business-use insurance confirmation</strong>
            <p>
              Bakkies must be insured for business use before they can continue through onboarding.
            </p>
            <div className="register-choice-actions">
              <button
                className={`register-choice-pill${driverVehicleDetails.insuranceBusinessUseConfirmed === true ? ' register-choice-pill-active' : ''}`}
                onClick={() =>
                  setDriverVehicleDetails((current) => ({
                    ...current,
                    insuranceBusinessUseConfirmed: true,
                  }))
                }
                type="button"
              >
                Yes
              </button>
              <button
                className={`register-choice-pill${driverVehicleDetails.insuranceBusinessUseConfirmed === false ? ' register-choice-pill-active' : ''}`}
                onClick={() =>
                  setDriverVehicleDetails((current) => ({
                    ...current,
                    insuranceBusinessUseConfirmed: false,
                  }))
                }
                type="button"
              >
                No
              </button>
            </div>
            {errors.insuranceBusinessUseConfirmed ? (
              <div className="register-inline-error">{errors.insuranceBusinessUseConfirmed}</div>
            ) : null}
          </div>
        ) : null}
      </div>

      {errors.submit ? <div className="register-inline-error">{errors.submit}</div> : null}

      <div className="register-flow-actions">
        <button className="register-secondary-button" onClick={() => setStage('personal-docs')} type="button">
          Back
        </button>
        <button
          className="register-submit register-submit-inline"
          disabled={driverStageLoading}
          onClick={handleDriverVehicleDetailsSubmit}
          type="button"
        >
          {driverStageLoading ? 'Saving vehicle...' : 'Save Vehicle Details'}
        </button>
      </div>
    </div>
  );

  const renderDriverVehiclePhotos = () => (
    <div className="register-flow-panel">
      <div className="register-form-badge">Vehicle Photos & Documents</div>
      <h2 className="register-form-title">Upload vehicle photos and documents</h2>
      <p className="register-form-copy">
        This matches the app flow: capture all four vehicle angles, then add the roadworthy, licence disk, PDP, and insurance letter with their dates.
      </p>

      <div className="register-section-card">
        <strong>Photo tips</strong>
        <div className="register-feature-list">
          <div className="register-feature-item">
            <span className="register-feature-icon">FR</span>
            <div>
              <div className="register-feature-title">Capture the full vehicle</div>
              <div className="register-feature-copy">Use good lighting and keep the vehicle fully inside the frame.</div>
            </div>
          </div>
          <div className="register-feature-item">
            <span className="register-feature-icon">NO</span>
            <div>
              <div className="register-feature-title">Avoid blur and glare</div>
              <div className="register-feature-copy">Make sure number plates and document text stay readable.</div>
            </div>
          </div>
          <div className="register-feature-item">
            <span className="register-feature-icon">DT</span>
            <div>
              <div className="register-feature-title">Dates matter</div>
              <div className="register-feature-copy">Use the dates exactly as printed on the roadworthy, licence disk, and PDP.</div>
            </div>
          </div>
        </div>
      </div>

      <div className="register-section-card">
        <strong>Required before you continue</strong>
        <div className="register-checklist-item">
          <span className={`register-checklist-dot${driverVehicleFiles.frontPhoto.file ? ' register-checklist-dot-complete' : ''}`} />
          <span>Front, left, right, and back vehicle photos</span>
        </div>
        <div className="register-checklist-item">
          <span className={`register-checklist-dot${driverVehicleFiles.roadworthy.file && driverVehicleFiles.roadworthyIssuedOn ? ' register-checklist-dot-complete' : ''}`} />
          <span>Roadworthy document and issue date</span>
        </div>
        <div className="register-checklist-item">
          <span className={`register-checklist-dot${driverVehicleFiles.licenceDisk.file && driverVehicleFiles.licenceDiskIssuedOn && driverVehicleFiles.licenceDiskValidTo ? ' register-checklist-dot-complete' : ''}`} />
          <span>Licence disk photo with issue and expiry dates</span>
        </div>
        <div className="register-checklist-item">
          <span className={`register-checklist-dot${driverVehicleFiles.pdp.file && driverVehicleFiles.pdpIssuedOn && driverVehicleFiles.pdpValidTo ? ' register-checklist-dot-complete' : ''}`} />
          <span>PDP document with issue and expiry dates</span>
        </div>
        <div className="register-checklist-item">
          <span className={`register-checklist-dot${driverVehicleFiles.insuranceLetter.file ? ' register-checklist-dot-complete' : ''}`} />
          <span>Insurance letter / proof</span>
        </div>
      </div>

      <div className="register-section-card">
        <strong>Vehicle photos</strong>
        <div className="register-field-group">
          {renderFilePicker('Front View *', 'image/*', setUploadField(setDriverVehicleFiles, 'frontPhoto'), driverVehicleFiles.frontPhoto.label, 'frontPhoto')}
          {renderFilePicker('Left Side *', 'image/*', setUploadField(setDriverVehicleFiles, 'leftPhoto'), driverVehicleFiles.leftPhoto.label, 'leftPhoto')}
          {renderFilePicker('Right Side *', 'image/*', setUploadField(setDriverVehicleFiles, 'rightPhoto'), driverVehicleFiles.rightPhoto.label, 'rightPhoto')}
          {renderFilePicker('Back View *', 'image/*', setUploadField(setDriverVehicleFiles, 'backPhoto'), driverVehicleFiles.backPhoto.label, 'backPhoto')}
        </div>
      </div>

      <div className="register-section-card">
        <strong>Vehicle documents</strong>
        <div className="register-field-group">
          {renderFilePicker('Roadworthy Document *', 'application/pdf', setUploadField(setDriverVehicleFiles, 'roadworthy'), driverVehicleFiles.roadworthy.label, 'roadworthy')}
          <div className="register-field">
            <label className="register-label" htmlFor="roadworthyIssuedOn">
              Roadworthy Issue Date <span className="register-required">*</span>
            </label>
          <input
            className="register-input"
            id="roadworthyIssuedOn"
            max={todayDateInputValue}
            onChange={setDriverVehicleFileValue('roadworthyIssuedOn')}
            type="date"
            value={driverVehicleFiles.roadworthyIssuedOn}
            />
            {errors.roadworthyIssuedOn ? <div className="register-inline-error">{errors.roadworthyIssuedOn}</div> : null}
          </div>

          {renderFilePicker('Licence Disk *', 'image/*', setUploadField(setDriverVehicleFiles, 'licenceDisk'), driverVehicleFiles.licenceDisk.label, 'licenceDisk')}
          <div className="register-field-inline">
            <div className="register-field">
              <label className="register-label" htmlFor="licenceDiskIssuedOn">
                Licence Disk Issue Date <span className="register-required">*</span>
              </label>
          <input
            className="register-input"
            id="licenceDiskIssuedOn"
            max={todayDateInputValue}
            onChange={setDriverVehicleFileValue('licenceDiskIssuedOn')}
            type="date"
            value={driverVehicleFiles.licenceDiskIssuedOn}
              />
            </div>
            <div className="register-field">
              <label className="register-label" htmlFor="licenceDiskValidTo">
                Licence Disk Expiry Date <span className="register-required">*</span>
              </label>
              <input
                className="register-input"
                id="licenceDiskValidTo"
                onChange={setDriverVehicleFileValue('licenceDiskValidTo')}
                type="date"
                value={driverVehicleFiles.licenceDiskValidTo}
              />
            </div>
          </div>
          {errors.licenceDiskIssuedOn ? <div className="register-inline-error">{errors.licenceDiskIssuedOn}</div> : null}

          {renderFilePicker('PDP Document *', 'application/pdf', setUploadField(setDriverVehicleFiles, 'pdp'), driverVehicleFiles.pdp.label, 'pdp')}
          <div className="register-field-inline">
            <div className="register-field">
              <label className="register-label" htmlFor="pdpIssuedOn">
                PDP Issue Date <span className="register-required">*</span>
              </label>
          <input
            className="register-input"
            id="pdpIssuedOn"
            max={todayDateInputValue}
            onChange={setDriverVehicleFileValue('pdpIssuedOn')}
            type="date"
            value={driverVehicleFiles.pdpIssuedOn}
              />
            </div>
            <div className="register-field">
              <label className="register-label" htmlFor="pdpValidTo">
                PDP Expiry Date <span className="register-required">*</span>
              </label>
              <input
                className="register-input"
                id="pdpValidTo"
                onChange={setDriverVehicleFileValue('pdpValidTo')}
                type="date"
                value={driverVehicleFiles.pdpValidTo}
              />
            </div>
          </div>
          {errors.pdpIssuedOn ? <div className="register-inline-error">{errors.pdpIssuedOn}</div> : null}

          {renderFilePicker(
            'Insurance Letter / Proof *',
            'application/pdf',
            setUploadField(setDriverVehicleFiles, 'insuranceLetter'),
            driverVehicleFiles.insuranceLetter.label,
            'insuranceLetter',
            'Upload proof of business-use or commercial cover.',
          )}
        </div>
      </div>

      {errors.submit ? <div className="register-inline-error">{errors.submit}</div> : null}

      <div className="register-flow-actions">
        <button className="register-secondary-button" onClick={() => setStage('vehicle-details')} type="button">
          Back
        </button>
        <button
          className="register-submit register-submit-inline"
          disabled={driverStageLoading}
          onClick={handleDriverVehicleFilesSubmit}
          type="button"
        >
          {driverStageLoading ? 'Uploading files...' : 'Save Vehicle Documents'}
        </button>
      </div>
    </div>
  );

  const renderDriverAccountType = () => (
    <div className="register-flow-panel">
      <div className="register-form-badge">Account Type</div>
      <h2 className="register-form-title">How do you operate?</h2>
      <p className="register-form-copy">
        Choose whether you are an individual owner or a company driver so we can route the final onboarding details correctly.
      </p>
      <div className="register-section-card">
        <strong>Choose the path that matches your real setup</strong>
        <div className="register-feature-list">
          <div className="register-feature-item">
            <span className="register-feature-icon">PAY</span>
            <div>
              <div className="register-feature-title">Payout path</div>
              <div className="register-feature-copy">Individual owners continue to bank details. Company drivers continue to company details.</div>
            </div>
          </div>
          <div className="register-feature-item">
            <span className="register-feature-icon">OPS</span>
            <div>
              <div className="register-feature-title">Operations fit</div>
              <div className="register-feature-copy">Pick the option that reflects how assignments, payments, and support should be handled.</div>
            </div>
          </div>
        </div>
      </div>

      <div className="register-account-type-grid">
        {DRIVER_ACCOUNT_TYPE_OPTIONS.map((option) => (
          <button
            className={`register-account-type-card${driverAccountType === option.value ? ' register-account-type-card-active' : ''}`}
            key={option.value}
            onClick={() => {
              setDriverAccountType(option.value);
              if (errors.accountType) {
                setErrors((current) => ({ ...current, accountType: '' }));
              }
            }}
            type="button"
          >
            <div className="register-account-type-top">
              <span className="register-account-type-badge">{option.badge}</span>
              <div className="register-account-type-copy">
                <div className="register-account-type-title">{option.title}</div>
                <div className="register-account-type-best-for">{option.bestFor}</div>
              </div>
            </div>
            <div className="register-account-type-description">{option.description}</div>
            <div className="register-account-type-points">
              {option.points.map((point) => (
                <div className="register-account-type-point" key={point}>
                  <span className="register-account-type-point-dot" />
                  <span>{point}</span>
                </div>
              ))}
            </div>
            <div className="register-account-type-footer">
              {driverAccountType === option.value ? 'Selected' : 'Tap to choose this path'}
            </div>
          </button>
        ))}
      </div>
      {errors.accountType ? <div className="register-inline-error">{errors.accountType}</div> : null}
      {errors.submit ? <div className="register-inline-error">{errors.submit}</div> : null}

      <div className="register-flow-actions">
        <button className="register-secondary-button" onClick={() => setStage('vehicle-photos')} type="button">
          Back
        </button>
        <button
          className="register-submit register-submit-inline"
          disabled={driverStageLoading}
          onClick={handleDriverAccountTypeSubmit}
          type="button"
        >
          {driverStageLoading ? 'Saving type...' : 'Save Account Type'}
        </button>
      </div>
    </div>
  );

  const renderDriverCompanyDetails = () => (
    <div className="register-flow-panel">
      <div className="register-form-badge">Company Details</div>
      <h2 className="register-form-title">Add the company details</h2>
      <p className="register-form-copy">
        Company drivers finish onboarding with the company name, contact email, and main contact numbers.
      </p>
      <div className="register-section-card">
        <strong>What we need from the company</strong>
        <div className="register-feature-list">
          <div className="register-feature-item">
            <span className="register-feature-icon">CO</span>
            <div>
              <div className="register-feature-title">Company identity</div>
              <div className="register-feature-copy">The business name that will own or manage this driver profile.</div>
            </div>
          </div>
          <div className="register-feature-item">
            <span className="register-feature-icon">EM</span>
            <div>
              <div className="register-feature-title">Primary email</div>
              <div className="register-feature-copy">A working email address for onboarding and review communication.</div>
            </div>
          </div>
          <div className="register-feature-item">
            <span className="register-feature-icon">PH</span>
            <div>
              <div className="register-feature-title">Contact numbers</div>
              <div className="register-feature-copy">At least one reachable phone number with country code.</div>
            </div>
          </div>
        </div>
      </div>
      <div className="register-section-card">
        <strong>Required before you continue</strong>
        <div className="register-checklist-item">
          <span className={`register-checklist-dot${driverCompanyDetails.companyName.trim() ? ' register-checklist-dot-complete' : ''}`} />
          <span>Company name</span>
        </div>
        <div className="register-checklist-item">
          <span className={`register-checklist-dot${driverCompanyDetails.companyEmail.trim() ? ' register-checklist-dot-complete' : ''}`} />
          <span>Company email</span>
        </div>
        <div className="register-checklist-item">
          <span className={`register-checklist-dot${driverCompanyDetails.companyPhone1.trim() ? ' register-checklist-dot-complete' : ''}`} />
          <span>Main company phone number</span>
        </div>
      </div>
      <div className="register-field-group">
        <div className="register-field">
          <label className="register-label" htmlFor="companyName">
            Company Name <span className="register-required">*</span>
          </label>
          <input className="register-input" id="companyName" onChange={setDriverCompanyValue('companyName')} placeholder="e.g. Nomvula Logistics" value={driverCompanyDetails.companyName} />
          <div className="register-field-hint">Use the registered or trading name that drivers operate under.</div>
          {errors.companyName ? <div className="register-inline-error">{errors.companyName}</div> : null}
        </div>
        <div className="register-field">
          <label className="register-label" htmlFor="companyEmail">
            Company Email <span className="register-required">*</span>
          </label>
          <input className="register-input" id="companyEmail" onChange={setDriverCompanyValue('companyEmail')} placeholder="accounts@company.com" type="email" value={driverCompanyDetails.companyEmail} />
          <div className="register-field-hint">We will use this for onboarding and company contact.</div>
          {errors.companyEmail ? <div className="register-inline-error">{errors.companyEmail}</div> : null}
        </div>
        <div className="register-field-inline">
          <div className="register-field">
            <label className="register-label" htmlFor="companyPhone1">
              Company Phone 1 <span className="register-required">*</span>
            </label>
            <input className="register-input" id="companyPhone1" onChange={setDriverCompanyValue('companyPhone1')} placeholder="27735246452" value={driverCompanyDetails.companyPhone1} />
            <div className="register-field-hint">Enter digits with country code, for example 27...</div>
            {errors.companyPhone1 ? <div className="register-inline-error">{errors.companyPhone1}</div> : null}
          </div>
          <div className="register-field">
            <label className="register-label" htmlFor="companyPhone2">
              Company Phone 2
            </label>
            <input className="register-input" id="companyPhone2" onChange={setDriverCompanyValue('companyPhone2')} placeholder="Optional" value={driverCompanyDetails.companyPhone2} />
            <div className="register-field-hint">Optional backup number for the company.</div>
            {errors.companyPhone2 ? <div className="register-inline-error">{errors.companyPhone2}</div> : null}
          </div>
        </div>
      </div>
      {errors.submit ? <div className="register-inline-error">{errors.submit}</div> : null}

      <div className="register-flow-actions">
        <button className="register-secondary-button" onClick={() => setStage('account-type')} type="button">
          Back
        </button>
        <button
          className="register-submit register-submit-inline"
          disabled={driverStageLoading}
          onClick={handleDriverCompanyDetailsSubmit}
          type="button"
        >
          {driverStageLoading ? 'Saving company...' : 'Save Company Details'}
        </button>
      </div>
    </div>
  );

  const renderDriverBankDetails = () => (
    <div className="register-flow-panel">
      <div className="register-form-badge">Bank Details</div>
      <h2 className="register-form-title">Add your banking details</h2>
      <p className="register-form-copy">
        Individual owners finish onboarding with payout details and a bank confirmation letter.
      </p>
      <div className="register-section-card">
        <strong>Required before you continue</strong>
        <div className="register-checklist-item">
          <span className={`register-checklist-dot${driverBankDetails.bankName.trim() ? ' register-checklist-dot-complete' : ''}`} />
          <span>Bank name</span>
        </div>
        <div className="register-checklist-item">
          <span className={`register-checklist-dot${driverBankDetails.accountNumber.trim() ? ' register-checklist-dot-complete' : ''}`} />
          <span>Account number</span>
        </div>
        <div className="register-checklist-item">
          <span className={`register-checklist-dot${driverBankDetails.accountHolderName.trim() ? ' register-checklist-dot-complete' : ''}`} />
          <span>Account holder name</span>
        </div>
        <div className="register-checklist-item">
          <span className={`register-checklist-dot${driverBankDetails.bankLetter.file ? ' register-checklist-dot-complete' : ''}`} />
          <span>Bank confirmation letter PDF</span>
        </div>
      </div>
      <div className="register-field-group">
        <div className="register-field">
          <label className="register-label" htmlFor="bankName">
            Bank Name <span className="register-required">*</span>
          </label>
          <input
            className="register-input"
            id="bankName"
            onChange={setDriverBankValue('bankName')}
            placeholder="e.g. Standard Bank"
            value={driverBankDetails.bankName}
          />
          <div className="register-field-hint">Use the bank where your driver payouts should be sent.</div>
          {errors.bankName ? <div className="register-inline-error">{errors.bankName}</div> : null}
        </div>
        <div className="register-field-inline">
          <div className="register-field">
            <label className="register-label" htmlFor="accountNumber">
              Account Number <span className="register-required">*</span>
            </label>
            <input
              className="register-input"
              id="accountNumber"
              inputMode="numeric"
              onChange={setDriverBankValue('accountNumber')}
              placeholder="Digits only"
              value={driverBankDetails.accountNumber}
            />
            <div className="register-field-hint">Enter the full account number without spaces.</div>
            {errors.accountNumber ? <div className="register-inline-error">{errors.accountNumber}</div> : null}
          </div>
          <div className="register-field">
            <label className="register-label" htmlFor="branchCode">
              Branch Code
            </label>
            <input
              className="register-input"
              id="branchCode"
              inputMode="numeric"
              onChange={setDriverBankValue('branchCode')}
              placeholder="Optional"
              value={driverBankDetails.branchCode}
            />
            <div className="register-field-hint">Optional if your bank account does not require it.</div>
          </div>
        </div>
        <div className="register-field">
          <label className="register-label" htmlFor="accountHolderName">
            Account Holder Name <span className="register-required">*</span>
          </label>
          <input
            className="register-input"
            id="accountHolderName"
            onChange={setDriverBankValue('accountHolderName')}
            placeholder="Name exactly as it appears at the bank"
            value={driverBankDetails.accountHolderName}
          />
          <div className="register-field-hint">This should match the bank account owner exactly.</div>
          {errors.accountHolderName ? <div className="register-inline-error">{errors.accountHolderName}</div> : null}
        </div>
        {renderFilePicker(
          'Bank Confirmation Letter *',
          'application/pdf',
          setUploadField(setDriverBankDetails, 'bankLetter'),
          driverBankDetails.bankLetter.label,
          'bankLetter',
          'Upload a PDF bank letter that confirms the account owner and account number.',
        )}
      </div>
      {errors.submit ? <div className="register-inline-error">{errors.submit}</div> : null}

      <div className="register-flow-actions">
        <button className="register-secondary-button" onClick={() => setStage('account-type')} type="button">
          Back
        </button>
        <button
          className="register-submit register-submit-inline"
          disabled={driverStageLoading}
          onClick={handleDriverBankDetailsSubmit}
          type="button"
        >
          {driverStageLoading ? 'Saving banking...' : 'Save Bank Details'}
        </button>
      </div>
    </div>
  );

  const renderDriverSubmitted = () => (
    <div className="register-flow-panel register-flow-panel-success">
      <div className="register-form-badge">Final Review</div>
      <h2 className="register-form-title">Submit your driver onboarding</h2>
      <p className="register-form-copy">
        Your account, identity checks, vehicle information, and final business details are now ready. Submit to complete web onboarding and then move into the app.
      </p>
      <div className="register-section-card">
        <strong>Ready to submit</strong>
        <div className="register-selected-town-meta">1. Personal documents uploaded</div>
        <div className="register-selected-town-meta">2. Vehicle details saved</div>
        <div className="register-selected-town-meta">3. Vehicle photos and documents uploaded</div>
        <div className="register-selected-town-meta">4. Account type and final payout/company details saved</div>
      </div>
      {errors.submit ? <div className="register-inline-error">{errors.submit}</div> : null}
      <div className="register-flow-actions">
        <button
          className="register-secondary-button"
          onClick={() => {
            if (isDriverOnboardingSubmittedOrCompleted) {
              navigate('/');
              return;
            }

            setStage(driverAccountType === 'COMPANY_DRIVER' ? 'company-details' : 'bank-details');
          }}
          type="button"
        >
          {isDriverOnboardingSubmittedOrCompleted ? 'Home' : 'Back'}
        </button>
        {!isDriverOnboardingSubmittedOrCompleted ? (
          <button
            className="register-submit register-submit-inline"
            disabled={driverStageLoading}
            onClick={handleDriverOnboardingSubmit}
            type="button"
          >
            {driverStageLoading ? 'Submitting...' : 'Submit Driver Onboarding'}
          </button>
        ) : null}
      </div>
    </div>
  );

  const renderBusinessOnboarding = () => {
    const status = businessProfile?.verificationStatus ?? 'DRAFT';
    const readonly = status === 'SUBMITTED' || status === 'APPROVED';

    return (
      <div className="register-flow-panel">
        <div className="register-form-badge">Business Customer</div>
        <h2 className="register-form-title">Set up your business profile</h2>
        <p className="register-form-copy">
          Add the business details admins need to approve this account. Branches are optional for companies with multiple locations.
        </p>

        {businessProfile?.verificationNote ? (
          <div className="register-inline-error">{businessProfile.verificationNote}</div>
        ) : null}

        <div className="register-section-card register-business-card">
          <div className="register-business-card-header">
            <div>
              <div className="register-section-title">Business details</div>
              <p className="register-form-copy">
                Legal name is prefilled from the business name used at signup. You can edit it if the registered name differs.
              </p>
            </div>
            <span className="register-business-step-pill">Required</span>
          </div>
          <div className="register-field-group">
            <div className="register-field">
              <label className="register-label" htmlFor="businessLegalName">
                Legal business name
              </label>
              <input
                className="register-input"
                disabled={readonly}
                id="businessLegalName"
                onChange={(event) => setBusinessProfileForm((current) => ({ ...current, legalName: event.target.value }))}
                placeholder="Registered company or sole proprietor name"
                value={businessProfileForm.legalName}
              />
              {errors.legalName ? <div className="register-inline-error">{errors.legalName}</div> : null}
            </div>

            <div className="register-field-group register-field-group-two">
              <div className="register-field">
                <label className="register-label" htmlFor="businessTradingName">
                  Trading name
                </label>
                <input
                  className="register-input"
                  disabled={readonly}
                  id="businessTradingName"
                  onChange={(event) => setBusinessProfileForm((current) => ({ ...current, tradingName: event.target.value }))}
                  placeholder="Optional"
                  value={businessProfileForm.tradingName}
                />
              </div>
              <div className="register-field">
                <label className="register-label" htmlFor="businessRegistrationNumber">
                  Registration number
                </label>
                <input
                  className="register-input"
                  disabled={readonly}
                  id="businessRegistrationNumber"
                  onChange={(event) => setBusinessProfileForm((current) => ({ ...current, registrationNumber: event.target.value }))}
                  placeholder="Optional"
                  value={businessProfileForm.registrationNumber}
                />
              </div>
            </div>

            <div className="register-field-group register-field-group-two">
              <div className="register-field">
                <label className="register-label" htmlFor="businessVatNumber">
                  VAT number
                </label>
                <input
                  className="register-input"
                  disabled={readonly}
                  id="businessVatNumber"
                  onChange={(event) => setBusinessProfileForm((current) => ({ ...current, vatNumber: event.target.value }))}
                  placeholder="Optional"
                  value={businessProfileForm.vatNumber}
                />
              </div>
              <div className="register-field">
                <label className="register-label" htmlFor="businessPhone">
                  Business phone
                </label>
                <input
                  className="register-input"
                  disabled={readonly}
                  id="businessPhone"
                  onChange={(event) => setBusinessProfileForm((current) => ({ ...current, businessPhone: event.target.value }))}
                  placeholder="e.g. +27711234567"
                  value={businessProfileForm.businessPhone}
                />
              </div>
            </div>

            <div className="register-field">
              <label className="register-label" htmlFor="businessEmail">
                Business email
              </label>
              <input
                className="register-input"
                disabled={readonly}
                id="businessEmail"
                onChange={(event) => setBusinessProfileForm((current) => ({ ...current, businessEmail: event.target.value }))}
                placeholder="ops@example.co.za"
                type="email"
                value={businessProfileForm.businessEmail}
              />
              {errors.businessEmail ? <div className="register-inline-error">{errors.businessEmail}</div> : null}
            </div>

            <div className="register-field">
              <label className="register-label" htmlFor="businessHeadOffice">
                Head office address
              </label>
              <input
                className="register-input"
                disabled={readonly}
                id="businessHeadOffice"
                onChange={(event) => setBusinessProfileForm((current) => ({ ...current, headOfficeAddress: event.target.value }))}
                placeholder="Street address"
                value={businessProfileForm.headOfficeAddress}
              />
            </div>

            <div className="register-field-group register-field-group-two">
              <div className="register-field">
                <label className="register-label" htmlFor="representativeName">
                  Primary contact name
                </label>
                <input
                  className="register-input"
                  disabled={readonly}
                  id="representativeName"
                  onChange={(event) => setBusinessProfileForm((current) => ({ ...current, representativeName: event.target.value }))}
                  placeholder="Person admins can contact about this account"
                  value={businessProfileForm.representativeName}
                />
              </div>
              <div className="register-field">
                <label className="register-label" htmlFor="representativeEmail">
                  Primary contact email
                </label>
                <input
                  className="register-input"
                  disabled={readonly}
                  id="representativeEmail"
                  onChange={(event) => setBusinessProfileForm((current) => ({ ...current, representativeEmail: event.target.value }))}
                  placeholder="contact@example.co.za"
                  type="email"
                  value={businessProfileForm.representativeEmail}
                />
                {errors.representativeEmail ? <div className="register-inline-error">{errors.representativeEmail}</div> : null}
              </div>
            </div>
          </div>

          {!readonly ? (
            <button className="register-submit register-submit-inline" disabled={businessLoading} onClick={handleBusinessProfileSubmit} type="button">
              {businessLoading ? 'Saving...' : 'Save business profile'}
            </button>
          ) : null}
        </div>

        <div className="register-section-card register-business-card">
          <div className="register-business-card-header">
            <div>
              <div className="register-section-title">Branches</div>
              <p className="register-form-copy">
                Optional. Add branches when bookings should identify a specific store, depot, warehouse, or office.
              </p>
            </div>
            <span className="register-business-step-pill register-business-step-pill-muted">Optional</span>
          </div>
          {businessProfile?.branches.length ? (
            <div className="register-feature-list">
              {businessProfile.branches.map((branch) => (
                <div className="register-feature-item" key={branch.id}>
                  <span className="register-feature-icon">{branch.defaultBranch ? 'HQ' : 'BR'}</span>
                  <div>
                    <div className="register-feature-title">{branch.branchName}</div>
                    <div className="register-feature-copy">{branch.address}</div>
                    {!readonly ? (
                      <div className="register-flow-actions register-flow-actions-compact">
                        <button
                          className="register-text-button"
                          onClick={() => {
                            setBusinessBranchForm({
                              id: branch.id,
                              branchName: branch.branchName || '',
                              address: branch.address || '',
                              contactEmail: branch.contactEmail || '',
                              contactPhone: branch.contactPhone || '',
                              defaultBranch: branch.defaultBranch,
                            });
                            setShowBusinessBranchForm(true);
                          }}
                          type="button"
                        >
                          Edit
                        </button>
                        <button className="register-text-button" onClick={() => void handleDeleteBusinessBranch(branch.id)} type="button">
                          Delete
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="register-empty-optional">
              <strong>No branches added</strong>
              <span>This works for a new or single-location business. You can submit without adding one.</span>
            </div>
          )}

          {!readonly && !showBusinessBranchForm ? (
            <button
              className="register-secondary-button register-optional-action"
              disabled={businessLoading || !businessProfile?.id}
              onClick={() => {
                setBusinessBranchForm({
                  id: '',
                  branchName: '',
                  address: '',
                  contactEmail: '',
                  contactPhone: '',
                  defaultBranch: false,
                });
                setShowBusinessBranchForm(true);
              }}
              type="button"
            >
              Add branch
            </button>
          ) : null}

          {!readonly && showBusinessBranchForm ? (
            <div className="register-field-group">
              <div className="register-business-card-header register-business-card-header-tight">
                <div className="register-section-title">
                  {businessBranchForm.id ? 'Edit branch' : 'Add branch'}
                </div>
                <button
                  className="register-text-button"
                  onClick={() => {
                    setBusinessBranchForm({
                      id: '',
                      branchName: '',
                      address: '',
                      contactEmail: '',
                      contactPhone: '',
                      defaultBranch: false,
                    });
                    setShowBusinessBranchForm(false);
                  }}
                  type="button"
                >
                  Cancel
                </button>
              </div>
              <div className="register-field-group register-field-group-two">
                <div className="register-field">
                  <label className="register-label" htmlFor="branchName">
                    Branch name
                  </label>
                  <input
                    className="register-input"
                    id="branchName"
                    onChange={(event) => setBusinessBranchForm((current) => ({ ...current, branchName: event.target.value }))}
                    placeholder="Main branch"
                    value={businessBranchForm.branchName}
                  />
                  {errors.branchName ? <div className="register-inline-error">{errors.branchName}</div> : null}
                </div>
                <div className="register-field">
                  <label className="register-label" htmlFor="branchPhone">
                    Branch phone
                  </label>
                  <input
                    className="register-input"
                    id="branchPhone"
                    onChange={(event) => setBusinessBranchForm((current) => ({ ...current, contactPhone: event.target.value }))}
                    placeholder="Optional"
                    value={businessBranchForm.contactPhone}
                  />
                </div>
              </div>
              <div className="register-field">
                <label className="register-label" htmlFor="branchAddress">
                  Branch address
                </label>
                <input
                  className="register-input"
                  id="branchAddress"
                  onChange={(event) => setBusinessBranchForm((current) => ({ ...current, address: event.target.value }))}
                  placeholder="Street address"
                  value={businessBranchForm.address}
                />
                {errors.branchAddress ? <div className="register-inline-error">{errors.branchAddress}</div> : null}
              </div>
              <div className="register-field">
                <label className="register-label" htmlFor="branchEmail">
                  Branch email
                </label>
                <input
                  className="register-input"
                  id="branchEmail"
                  onChange={(event) => setBusinessBranchForm((current) => ({ ...current, contactEmail: event.target.value }))}
                  placeholder="Optional"
                  type="email"
                  value={businessBranchForm.contactEmail}
                />
                {errors.branchContactEmail ? <div className="register-inline-error">{errors.branchContactEmail}</div> : null}
              </div>
              <label className="register-checkbox-row">
                <input
                  checked={businessBranchForm.defaultBranch}
                  onChange={(event) => setBusinessBranchForm((current) => ({ ...current, defaultBranch: event.target.checked }))}
                  type="checkbox"
                />
                <span>Default branch</span>
              </label>
              <button className="register-secondary-button" disabled={businessLoading || !businessProfile?.id} onClick={handleBusinessBranchSubmit} type="button">
                {businessBranchForm.id ? 'Update branch' : 'Add branch'}
              </button>
            </div>
          ) : null}
        </div>

        {errors.submit ? <div className="register-inline-error">{errors.submit}</div> : null}

        <div className="register-flow-actions">
          <button
            className="register-submit register-submit-inline"
            disabled={businessLoading || readonly || !businessProfile?.id}
            onClick={handleSubmitBusinessOnboarding}
            type="button"
          >
            {businessLoading ? 'Submitting...' : 'Submit for review'}
          </button>
        </div>
      </div>
    );
  };

  const renderBusinessSubmitted = () => (
    <div className="register-flow-panel register-status-panel">
      <div className="register-status-badge">Business Review</div>
      <h2 className="register-form-title">
        {businessProfile?.verificationStatus === 'APPROVED' ? 'Your business profile is approved.' : 'Your business profile is in review.'}
      </h2>
      <p className="register-form-copy">
        {businessProfile?.verificationStatus === 'APPROVED'
          ? 'You can continue with the user app and book as a business customer.'
          : 'An admin will review your business details. You can use the user app while business bookings wait for approval.'}
      </p>
      <div className="register-section-card">
        <div className="register-feature-title">{businessProfile?.legalName || 'Business profile'}</div>
        <div className="register-feature-copy">Status: {businessProfile?.verificationStatus || 'SUBMITTED'}</div>
        {businessProfile?.submittedAt ? <div className="register-feature-copy">Submitted: {businessProfile.submittedAt}</div> : null}
      </div>
      <div className="register-flow-actions">
        <a className="register-submit register-submit-inline" href={USER_APP_URL} rel="noreferrer" target="_blank">
          <GooglePlayIcon />
          Get it on Google Play
        </a>
        <button className="register-secondary-button" onClick={() => navigate('/')} type="button">
          Go to Home
        </button>
      </div>
    </div>
  );

  const renderAccountForm = () => (
    <>
      <div className="register-form-header">
        <div className="register-form-badge">
          {selectedRole === 'driver' ? 'Driver Registration' : 'Customer Registration'}
        </div>
        <h2 className="register-form-title">
          {selectedRole === 'customer' && customerAccountType === 'BUSINESS'
            ? 'Create a business account'
            : 'Create account'}
        </h2>
        <p className="register-form-copy">
          {selectedRole === 'driver'
            ? 'Start here, then confirm your operating town and continue onboarding.'
            : customerAccountType === 'BUSINESS'
              ? 'Start with the business name and email. After email verification, you’ll add business details for review.'
              : 'Create your account to continue to booking and verification.'}
        </p>
      </div>

      <form className="register-form" onSubmit={handleAccountSubmit}>
        {!isExplicitCustomerRegistration ? renderRoleCards() : null}

        {selectedRole === 'customer' ? (
          <fieldset className="register-role-group">
            <legend className="register-section-title">Customer account type</legend>
            <div className="register-segmented-control">
              {[
                {
                  key: 'INDIVIDUAL',
                  title: 'Individual',
                  description: 'Personal moves, appliance deliveries, and home errands.',
                  badge: 'I',
                },
                {
                  key: 'BUSINESS',
                  title: 'Business',
                  description: 'Company moves, stock, office equipment, and branch deliveries.',
                  badge: 'B',
                },
              ].map((option) => {
                const active = customerAccountType === option.key;
                return (
                  <button
                    aria-pressed={active}
                    className={`register-segmented-option${active ? ' register-segmented-option-active' : ''}`}
                    key={option.key}
                    onClick={() => setCustomerAccountType(option.key as 'INDIVIDUAL' | 'BUSINESS')}
                    type="button"
                  >
                    <span className="register-segmented-icon">{option.badge}</span>
                    <span className="register-segmented-text">
                      <span>{option.title}</span>
                      <small>{option.description}</small>
                    </span>
                  </button>
                );
              })}
            </div>
            {customerAccountType === 'BUSINESS' ? (
              <div className="register-context-note">
                Business details and optional branches are added after email verification.
              </div>
            ) : null}
          </fieldset>
        ) : null}

        <div className="register-field-group">
          <div className="register-field">
            <label className="register-label" htmlFor="fullName">
              {selectedRole === 'customer' && customerAccountType === 'BUSINESS' ? 'Business name' : 'Full name'}
            </label>
            <input
              className="register-input"
              id="fullName"
              onChange={setFieldValue('fullName')}
              placeholder={
                selectedRole === 'customer' && customerAccountType === 'BUSINESS'
                  ? 'e.g. Mike Innovations Pty Ltd'
                  : 'John Doe'
              }
              required
              type="text"
              value={formData.fullName}
            />
            {errors.fullName ? <div className="register-inline-error">{errors.fullName}</div> : null}
          </div>

          <div className="register-field">
            <label className="register-label" htmlFor="email">
              Email Address
            </label>
            <input autoCapitalize="none" className="register-input" id="email" onChange={setFieldValue('email')} placeholder="john@example.com" required type="email" value={formData.email} />
            {errors.email ? <div className="register-inline-error">{errors.email}</div> : null}
          </div>

          <div className="register-field">
            <label className="register-label" htmlFor="password">
              Password
            </label>
            <div className="register-password-shell">
              <input className="register-input register-input-password" id="password" minLength={8} onChange={setFieldValue('password')} placeholder="Minimum 8 characters" required type={showPassword ? 'text' : 'password'} value={formData.password} />
              <button aria-label={showPassword ? 'Hide password' : 'Show password'} className="register-password-toggle" onClick={() => setShowPassword((current) => !current)} type="button">
                {showPassword ? (
                  <svg aria-hidden="true" className="register-password-icon" fill="none" viewBox="0 0 24 24">
                    <path d="M3 3l18 18" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
                    <path d="M10.58 10.58A2 2 0 0013.42 13.42" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
                    <path d="M9.88 5.09A10.94 10.94 0 0112 4.9c5.05 0 8.27 4.13 9 5.1a15.63 15.63 0 01-4.05 3.81" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
                    <path d="M6.61 6.61C4.62 7.84 3.32 9.51 3 10c.5.66 2.15 2.79 4.82 4.13A11.8 11.8 0 0012 15.1c.64 0 1.25-.05 1.83-.14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
                  </svg>
                ) : (
                  <svg aria-hidden="true" className="register-password-icon" fill="none" viewBox="0 0 24 24">
                    <path d="M2.46 12.29C3.74 10.18 6.89 6 12 6s8.26 4.18 9.54 6.29a.56.56 0 010 .58C20.26 14.98 17.11 19 12 19s-8.26-4.02-9.54-6.13a.56.56 0 010-.58z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
                    <circle cx="12" cy="12.5" r="3" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
                  </svg>
                )}
              </button>
            </div>
            <div className="register-field-hint">Use uppercase, lowercase, and at least one number.</div>
            {errors.password ? <div className="register-inline-error">{errors.password}</div> : null}
          </div>
        </div>

        {errors.submit ? <div className="register-inline-error">{errors.submit}</div> : null}

        <button className="register-submit" disabled={loading} type="submit">
          {loading
            ? 'Creating account...'
            : selectedRole === 'customer' && customerAccountType === 'BUSINESS'
              ? 'Create business account'
              : 'Create account'}
        </button>

        <div className="register-divider">
          <span>or</span>
        </div>

        <a className="register-google-button" href={googleSignUpUrl}>
          <svg aria-hidden="true" className="register-google-icon" viewBox="0 0 18 18">
            <path
              d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
              fill="#4285F4"
            />
            <path
              d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.33-1.58-5.04-3.7H.94v2.33A9 9 0 0 0 9 18z"
              fill="#34A853"
            />
            <path
              d="M3.96 10.72A5.41 5.41 0 0 1 3.68 9c0-.6.1-1.18.28-1.72V4.95H.94A9 9 0 0 0 0 9c0 1.45.34 2.82.94 4.05l3.02-2.33z"
              fill="#FBBC05"
            />
            <path
              d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A8.66 8.66 0 0 0 9 0 9 9 0 0 0 .94 4.95l3.02 2.33C4.67 5.16 6.66 3.58 9 3.58z"
              fill="#EA4335"
            />
          </svg>
          <span>Continue with Google</span>
        </a>

        <p className="register-login-copy">
          Already have an account?{' '}
          <button className="register-login-link" onClick={() => navigate(`/login?role=${selectedRole}`)} type="button">
            Log in
          </button>
        </p>
      </form>
    </>
  );

  const renderDriverLocationForm = () => (
    <div className="register-flow-panel">
      <div className="register-form-badge">Operating Town</div>
      <h2 className="register-form-title">Where do you operate?</h2>
      <p className="register-form-copy">
        Choose the town where you normally take assignments. We use it to place your driver profile in the right area.
      </p>

      <div className="register-field-group">
        <div className="register-field">
          <label className="register-label" htmlFor="driverCountry">
            Operating Country
          </label>
          <input className="register-input" id="driverCountry" readOnly type="text" value={driverLocation.country} />
        </div>

        <div className="register-field">
          <label className="register-label" htmlFor="driverProvince">
            Province
          </label>
          <select className="register-input register-select" id="driverProvince" onChange={setLocationField('province')} value={driverLocation.province}>
            {ZA_PROVINCES.map((province) => (
              <option key={province} value={province}>
                {province}
              </option>
            ))}
          </select>
          {errors.province ? <div className="register-inline-error">{errors.province}</div> : null}
        </div>

        <div className="register-field">
          <label className="register-label" htmlFor="driverTownSearch">
            Operating Town
          </label>
          <button
            className="register-picker-button"
            id="driverTownSearch"
            onClick={() => setShowTownSearchModal(true)}
            type="button"
          >
            <span className={pickedDriverTown ? 'register-picker-value' : 'register-picker-placeholder'}>
              {pickedDriverTown ? pickedDriverTown.town : 'Search your town, for example Sandton'}
            </span>
            <span className="register-picker-action">Search</span>
          </button>
          <div className="register-field-hint">
            Search and select a real town so we store the same coordinates the driver app uses.
          </div>
          {errors.town ? <div className="register-inline-error">{errors.town}</div> : null}
        </div>
      </div>

      {pickedDriverTown ? (
        <div className="register-selected-town-card">
          <div className="register-selected-town-header">
            <strong>Selected town</strong>
            <button
              className="register-text-button"
              onClick={() => {
                setPickedDriverTown(null);
                setShowTownSearchModal(true);
              }}
              type="button"
            >
              Change town
            </button>
          </div>
          <div className="register-selected-town-name">{pickedDriverTown.town}</div>
          <div className="register-selected-town-meta">{pickedDriverTown.address}</div>
          <div className="register-selected-town-meta">
            Lat: {pickedDriverTown.lat.toFixed(6)} | Lng: {pickedDriverTown.lng.toFixed(6)}
          </div>
        </div>
      ) : null}

      {errors.submit ? <div className="register-inline-error">{errors.submit}</div> : null}

      <div className="register-flow-actions">
        <button className="register-secondary-button" onClick={() => setStage('account')} type="button">
          Back
        </button>
        <button className="register-submit register-submit-inline" disabled={locationLoading} onClick={handleDriverLocationSubmit} type="button">
          {locationLoading ? 'Creating driver account...' : 'Create Driver Account'}
        </button>
      </div>
    </div>
  );

  const renderFaceLiveness = () => (
    <div className="register-flow-panel">
      <div className="register-form-badge">Face Liveness</div>
      <h2 className="register-form-title">Verify your face</h2>
      <p className="register-form-copy">
        {selectedRole === 'driver'
          ? 'Follow the guided poses below so we can confirm a live face capture for your driver profile.'
          : 'Follow the guided poses below so we can confirm a live face capture for your customer account.'}
      </p>

      <div className="register-liveness-progress">
        {LIVENESS_STEPS.map((stepConfig, index) => {
          const complete = Boolean(captures[stepConfig.key]);
          const active = index === currentLivenessIndex;
          return (
            <button className={`register-liveness-pill${active ? ' register-liveness-pill-active' : ''}${complete ? ' register-liveness-pill-complete' : ''}`} key={stepConfig.key} onClick={() => setCurrentLivenessIndex(index)} type="button">
              {complete ? 'Done' : stepConfig.title}
            </button>
          );
        })}
      </div>

      <div className="register-camera-shell">
        {cameraError ? (
          <div className="register-camera-error">{cameraError}</div>
        ) : (
          <>
            <video autoPlay className="register-camera-video" muted playsInline ref={videoRef} />
            <div className="register-camera-overlay">
              <div className="register-camera-frame" />
              <div className="register-camera-text">
                <strong>{currentLivenessStep.title}</strong>
                <span>{currentLivenessStep.hint}</span>
              </div>
            </div>
          </>
        )}
        <canvas className="register-hidden-canvas" ref={canvasRef} />
      </div>

      <div className="register-capture-grid">
        {LIVENESS_STEPS.map((stepConfig) => {
          const capture = captures[stepConfig.key];
          return (
            <div className="register-capture-card" key={stepConfig.key}>
              <div className="register-capture-label">{stepConfig.title}</div>
              {capture ? <img alt={stepConfig.title} className="register-capture-preview" src={capture.previewUrl} /> : <div className="register-capture-placeholder">Capture pending</div>}
            </div>
          );
        })}
      </div>

      <div className="register-flow-actions">
        <button className="register-secondary-button" disabled={capturing || Boolean(cameraError)} onClick={captureCurrentPose} type="button">
          {capturing ? 'Capturing...' : `Capture ${currentLivenessStep.title}`}
        </button>
        <button className="register-submit register-submit-inline" disabled={!livenessComplete || uploadingLiveness} onClick={uploadLivenessPack} type="button">
          {uploadingLiveness ? 'Uploading...' : selectedRole === 'driver' ? 'Continue to personal documents' : 'Continue to phone verification'}
        </button>
      </div>
    </div>
  );

  const renderPhoneVerification = () => (
    <div className="register-flow-panel">
      <div className="register-form-badge">Phone Verification</div>
      <h2 className="register-form-title">Add your phone number</h2>
      <p className="register-form-copy">
        {selectedRole === 'driver'
          ? 'We’ll send a verification code to confirm the number you want to use for assignment updates and account security.'
          : 'We’ll send a verification code to confirm your phone number for trip updates and account security.'}
      </p>

      <div className="register-field-group">
        <div className="register-field">
          <label className="register-label" htmlFor="phoneNumber">
            Phone Number
          </label>
          <input className="register-input" id="phoneNumber" inputMode="tel" onChange={(event) => {
            setPhone(event.target.value.replace(/[^\d]/g, ''));
            if (phoneError) setPhoneError('');
          }} placeholder="e.g. 27711234567" type="tel" value={phone} />
          <div className="register-field-hint">Include your country code, for example 27.</div>
          {phoneError ? <div className="register-inline-error">{phoneError}</div> : null}
        </div>
      </div>

      <div className="register-flow-actions">
        <button className="register-submit register-submit-inline" disabled={phoneLoading} onClick={handleSendPhoneCode} type="button">
          {phoneLoading ? 'Sending code...' : 'Send verification code'}
        </button>
      </div>
    </div>
  );

  return (
    <main className="register-page">
      <div className="register-shell">
        <aside className="register-panel register-panel-brand">
          <div className="register-brand-lockup">
            <div className="register-brand-badge">ShiftWagon</div>
            <h1 className="register-brand-title">
              {selectedRole === 'driver'
                ? driverSelfServiceOpen
                  ? 'Driver onboarding, simplified.'
                  : 'Approved transport partner onboarding.'
                : 'Move goods without guessing.'}
            </h1>
            <p className="register-brand-copy">
              {selectedRole === 'driver'
                ? driverSelfServiceOpen
                  ? 'A cleaner path to profile setup, verification, and vehicle onboarding.'
                  : 'Driver access is currently guided by our team for selected operators and partner drivers.'
                : 'Create an account, verify your details, and continue from the user app.'}
            </p>
          </div>

          {selectedRole === 'customer' ? (
            <div className="register-brand-proof-list">
              <div className="register-brand-proof-item">
                <strong>Verified customer accounts</strong>
                <span>Email, face, and phone checks protect bookings.</span>
              </div>
              <div className="register-brand-proof-item">
                <strong>Business profiles reviewed by admins</strong>
                <span>Companies can add branches after the account is verified.</span>
              </div>
            </div>
          ) : (
            <>
              <div className="register-brand-stat-grid">
                <div className="register-brand-stat-card">
                  <strong>{driverSelfServiceOpen ? 'Town-first' : 'Partner-led'}</strong>
                  <span>
                    {driverSelfServiceOpen
                      ? 'Operating area confirmed early'
                      : 'Guided access for approved operators'}
                  </span>
                </div>
                <div className="register-brand-stat-card">
                  <strong>Verified profile</strong>
                  <span>
                    {driverSelfServiceOpen
                      ? 'Documents, face, and phone checks'
                      : 'Verification details are shared after contact'}
                  </span>
                </div>
              </div>

              <div className="register-highlight-card">
                <div className="register-highlight-row">
                  <span className="register-highlight-icon">{selectedRoleData.badge}</span>
                  <div>
                    <div className="register-highlight-title">{selectedRoleData.title}</div>
                    <div className="register-highlight-note">{selectedRoleData.note}</div>
                  </div>
                </div>
                <p className="register-highlight-copy">{selectedRoleData.description}</p>
              </div>

              <div className="register-benefit-list">
                <div className="register-benefit-item">
                  <span className="register-benefit-dot" />
                  <span>
                    {driverSelfServiceOpen
                      ? 'Pick your town before the driver profile is created.'
                      : 'Contact us to discuss your driver or fleet onboarding route.'}
                  </span>
                </div>
                <div className="register-benefit-item">
                  <span className="register-benefit-dot" />
                  <span>
                    {driverSelfServiceOpen
                      ? 'Phone and identity checks keep the onboarding clean.'
                      : 'Approved drivers receive the correct onboarding instructions directly.'}
                  </span>
                </div>
                <div className="register-benefit-item">
                  <span className="register-benefit-dot" />
                  <span>
                    {driverSelfServiceOpen
                      ? 'Finish onboarding, then move into the driver app.'
                      : 'The driver app is provided through ShiftWagon after approval.'}
                  </span>
                </div>
              </div>

              <div className="register-app-card">
                <div className="register-app-card-title">
                  {driverSelfServiceOpen ? 'Driver app' : 'Transport partner access'}
                </div>
                <div className="register-app-card-copy">
                  {driverSelfServiceOpen
                    ? 'Download the driver app after submission.'
                    : 'Contact us to begin approved driver or fleet onboarding.'}
                </div>
                {!driverSelfServiceOpen ? (
                  <Link className="register-app-card-link" to={DRIVER_PARTNER_CONTACT_PATH}>
                    Contact ShiftWagon
                  </Link>
                ) : (
                  <a className="register-app-card-link" href={DRIVER_APP_URL} rel="noreferrer" target="_blank">
                    Download Driver App
                  </a>
                )}
              </div>
            </>
          )}
        </aside>

        <div className="register-panel register-panel-form">
          {isDriverPublicOnboardingClosed ? null : renderDriverProgress()}

          <div className="register-top-actions">
            <Link className="register-home-link" to="/">
              &larr; Home
            </Link>
          </div>

          {(stage === 'verify-email' || stage === 'phone-verification' || stage === 'face-liveness' || stage === 'personal-docs' || stage === 'vehicle-details' || stage === 'vehicle-photos' || stage === 'account-type' || stage === 'company-details' || stage === 'bank-details' || stage === 'submitted') && (
            <button className="register-reset-button" onClick={resetFlow} type="button">
              Start again
            </button>
          )}

          {isDriverPublicOnboardingClosed && renderDriverAccessMessage()}
          {!isDriverPublicOnboardingClosed && stage === 'account' && renderAccountForm()}
          {!isDriverPublicOnboardingClosed && stage === 'driver-location' && renderDriverLocationForm()}
          {!isDriverPublicOnboardingClosed && stage === 'business-profile' && renderBusinessOnboarding()}
          {!isDriverPublicOnboardingClosed && stage === 'business-submitted' && renderBusinessSubmitted()}
          {!isDriverPublicOnboardingClosed && stage === 'face-liveness' && renderFaceLiveness()}
          {!isDriverPublicOnboardingClosed && stage === 'phone-verification' && renderPhoneVerification()}
          {!isDriverPublicOnboardingClosed && stage === 'personal-docs' && renderDriverPersonalDocs()}
          {!isDriverPublicOnboardingClosed && stage === 'vehicle-details' && renderDriverVehicleDetails()}
          {!isDriverPublicOnboardingClosed && stage === 'vehicle-photos' && renderDriverVehiclePhotos()}
          {!isDriverPublicOnboardingClosed && stage === 'account-type' && renderDriverAccountType()}
          {!isDriverPublicOnboardingClosed && stage === 'company-details' && renderDriverCompanyDetails()}
          {!isDriverPublicOnboardingClosed && stage === 'bank-details' && renderDriverBankDetails()}
          {!isDriverPublicOnboardingClosed && stage === 'submitted' && renderDriverSubmitted()}
        </div>
      </div>

      {showEmailModal ? (
        <div className="register-modal-backdrop" role="presentation">
          <div className="register-modal" role="dialog" aria-modal="true" aria-labelledby="email-modal-title">
            <div className="register-status-badge">Verify Email</div>
            <h2 className="register-modal-title" id="email-modal-title">
              Enter the code from your email
            </h2>
            <p className="register-modal-copy">We sent a 6-digit code to {formData.email.trim().toLowerCase()}.</p>
            <input className="register-input register-modal-input" inputMode="numeric" maxLength={6} onChange={(event) => {
              setEmailCode(event.target.value.replace(/\D/g, ''));
              if (emailError) setEmailError('');
            }} placeholder="123456" value={emailCode} />
            {emailError ? <div className="register-inline-error">{emailError}</div> : null}
            <div className="register-modal-actions">
              <button className="register-submit register-submit-inline" disabled={emailLoading} onClick={handleVerifyEmail} type="button">
                {emailLoading ? 'Verifying...' : 'Verify email'}
              </button>
              <button className="register-text-button" disabled={emailLoading} onClick={handleResendEmail} type="button">
                Resend code
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showPhoneModal ? (
        <div className="register-modal-backdrop" role="presentation">
          <div className="register-modal" role="dialog" aria-modal="true" aria-labelledby="phone-modal-title">
            <div className="register-status-badge">Verify Phone</div>
            <h2 className="register-modal-title" id="phone-modal-title">
              Enter the code from your phone
            </h2>
            <p className="register-modal-copy">We sent a 6-digit code to {phone}.</p>
            <input className="register-input register-modal-input" inputMode="numeric" maxLength={6} onChange={(event) => {
              setPhoneCode(event.target.value.replace(/\D/g, ''));
              if (phoneError) setPhoneError('');
            }} placeholder="123456" value={phoneCode} />
            {phoneError ? <div className="register-inline-error">{phoneError}</div> : null}
            <div className="register-modal-actions">
              <button className="register-submit register-submit-inline" disabled={phoneLoading} onClick={handleVerifyPhone} type="button">
                {phoneLoading ? 'Verifying...' : 'Verify phone'}
              </button>
              <button className="register-text-button" disabled={phoneLoading} onClick={handleSendPhoneCode} type="button">
                Resend code
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showTownSearchModal ? (
        <div className="register-modal-backdrop" role="presentation">
          <div className="register-modal" role="dialog" aria-modal="true" aria-labelledby="town-modal-title">
            <div className="register-status-badge">Search Town</div>
            <h2 className="register-modal-title" id="town-modal-title">
              Choose your operating town
            </h2>
            <p className="register-modal-copy">
              Search within {driverLocation.province}. Pick a real result so we save exact coordinates
              for your driver profile.
            </p>
            <input
              autoFocus
              className="register-input register-modal-input"
              onChange={(event) => setTownSearchQuery(event.target.value)}
              placeholder={`Search a town in ${driverLocation.province}`}
              value={townSearchQuery}
            />
            <div className="register-town-results">
              {townSearchLoading ? <div className="register-town-empty">Loading towns...</div> : null}
              {!townSearchLoading && !townPredictions.length && townSearchQuery.trim() ? (
                <div className="register-town-empty">
                  No matching towns yet. Try a nearby town name or adjust the province.
                </div>
              ) : null}
              {townPredictions.map((prediction) => (
                <button
                  className="register-town-result"
                  key={prediction.placeId}
                  onClick={() => void handleDriverTownSelection(prediction)}
                  type="button"
                >
                  <strong>{prediction.primaryText}</strong>
                  <span>{prediction.secondaryText || prediction.description}</span>
                </button>
              ))}
            </div>
            <div className="register-modal-actions">
              <button
                className="register-secondary-button"
                onClick={() => {
                  setShowTownSearchModal(false);
                  setTownSearchQuery('');
                  setTownPredictions([]);
                }}
                type="button"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {snackbar ? <div className={`register-snackbar register-snackbar-${snackbar.tone}`}>{snackbar.message}</div> : null}
    </main>
  );
};

export default RegisterPage;
